// src/components/avatar/seasons/seasonArt.js
//
// THE SEASONAL WARDROBE — everything sold under the Seasons button, drawn as
// SVG from the motif kit (seasonMotifs.js) and a few shape generators, so a
// whole season is a short list of settings rather than hundreds of drawings.
//
// Shop things (worn in the outfit, placed by wearableArt.js SLOT_BOX):
//   SEASON_ART[artKey]        hats, wings, pets, frames — 100x100 box, same
//                              conventions as wearableArt.js
// Avatar-maker things (drawn inside AvatarFace's 100x100 box):
//   <SeasonTop style rid />   clothes — replaces avatarWear.js Top
//   outfitLegs(style)         colours for the full-body view
//   <SeasonEyewear style rid/> glasses & sunglasses on the eyes
//   <SeasonPin style x y scale/> a pin on the chest
//   <HairDeco style hair />   lights / clips / crowns on the hair
//   <SeasonBg style />        a 100x100 background scene
//   <SeasonBgTall style />    the same scene in a 100x170 box (full body)
//
// Item ids, names and prices: seasonCatalog.json. No known characters —
// the reindeer has a plain brown nose, the snowman is just a snowman.

import React from "react";
import { INK, MOTIFS, SMALL, Stamp, heartPath, motif, starPath } from "./seasonMotifs";

const S = { stroke: INK, strokeWidth: 3, strokeLinejoin: "round", strokeLinecap: "round" };

export const shade = (hex, f) => {
  const n = parseInt(String(hex).slice(1), 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const m = f > 1 ? (v) => v + (255 - v) * (f - 1) : (v) => v * f;
  return `#${[n >> 16, (n >> 8) & 255, n & 255].map((v) => ch(m(v)).toString(16).padStart(2, "0")).join("")}`;
};

const rnd = (i) => {
  const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/** point on a quadratic bezier */
const qpt = (p0, p1, p2, t) => [
  (1 - t) * (1 - t) * p0[0] + 2 * (1 - t) * t * p1[0] + t * t * p2[0],
  (1 - t) * (1 - t) * p0[1] + 2 * (1 - t) * t * p1[1] + t * t * p2[1],
];

const M = (m) => (Array.isArray(m) ? motif(m[0], m[1]) : null);
const Put = ({ m, x, y, s = 1, r = 0 }) => {
  const art = M(m);
  return art ? <g transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>{art}</g> : null;
};

// ===========================================================================
// HATS — sitting on the bottom of the 100x100 box (head top ≈ y 96)
// ===========================================================================

const Boppers = ({ band = "#2a2130", m, m2 }) => (
  <g>
    <path d="M12 98 Q50 60 88 98" stroke={INK} strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M12 98 Q50 60 88 98" stroke={band} strokeWidth="5.5" fill="none" strokeLinecap="round" />
    {[
      [34, 76, 28, 34],
      [66, 76, 72, 34],
    ].map(([x0, y0, x1, y1], i) => {
      const pts = [];
      for (let k = 0; k <= 8; k += 1) {
        const t = k / 8;
        pts.push(`${(x0 + (x1 - x0) * t + (k % 2 ? 3 : -3) * (k > 0 && k < 8 ? 1 : 0)).toFixed(1)} ${(y0 + (y1 - y0) * t).toFixed(1)}`);
      }
      return <path key={i} d={`M${pts.join(" L")}`} stroke={INK} strokeWidth="2.2" fill="none" strokeLinejoin="round" />;
    })}
    <Put m={m} x={28} y={24} s={2.7} r={-10} />
    <Put m={m2 || m} x={72} y={24} s={2.7} r={10} />
  </g>
);

const Beanie = ({ c, cuff, stripe, pom = "#fff", emblem, dots }) => (
  <g>
    <g {...S}>
      <circle cx="50" cy="34" r="11" fill={pom} />
      <path d="M16 90 C14 40 86 40 84 90 Z" fill={c} />
    </g>
    {stripe && <path d="M19 66 Q50 56 81 66" stroke={stripe} strokeWidth="6" fill="none" />}
    {dots && [26, 38, 50, 62, 74].map((x) => <circle key={x} cx={x} cy={x === 50 ? 52 : 56} r="2.6" fill={dots} />)}
    <path d="M30 50 Q38 44 46 44" stroke="#fff" strokeWidth="3" fill="none" opacity="0.4" strokeLinecap="round" />
    <rect x="12" y="78" width="76" height="20" rx="8" fill={cuff || shade(c, 0.85)} {...S} />
    {[20, 28, 36, 44, 52, 60, 68, 76].map((x) => (
      <path key={x} d={`M${x + 2} 82 L${x + 2} 94`} stroke={shade(cuff || c, 0.75)} strokeWidth="2" />
    ))}
    {emblem && <Put m={emblem} x={50} y={64} s={1.9} />}
  </g>
);

const ConeHat = ({ c, c2, tip = "#fff", m, stripes = true }) => (
  <g>
    <g {...S}>
      <path d="M50 16 L78 94 L22 94 Z" fill={c} />
    </g>
    {stripes && <path d="M38 50 L62 50 M31 72 L69 72" stroke={c2} strokeWidth="6" />}
    <path d="M50 16 L78 94 L22 94 Z" fill="none" {...S} />
    {m && <Put m={m} x={50} y={61} s={1.5} />}
    <path d="M18 94 Q50 104 82 94" stroke={INK} strokeWidth="10" fill="none" strokeLinecap="round" />
    <path d="M18 94 Q50 104 82 94" stroke={c2} strokeWidth="6" fill="none" strokeLinecap="round" />
    <circle cx="50" cy="13" r="8" fill={tip} {...S} />
  </g>
);

const TopHat = ({ c = "#26232e", band, emblem, stripes, starsOn }) => (
  <g>
    <g {...S}>
      <ellipse cx="50" cy="90" rx="40" ry="9" fill={shade(c, 0.85)} />
      <path d="M29 88 L31 30 Q50 25 69 30 L71 88 Q50 94 29 88 Z" fill={c} />
    </g>
    {stripes &&
      [36, 48, 60].map((y) => (
        <path key={y} d={`M30.5 ${y} Q50 ${y + 3} 69.5 ${y}`} stroke={stripes} strokeWidth="5" fill="none" />
      ))}
    <path d="M29.6 70 Q50 75 70.4 70 L70.8 82 Q50 87 29.2 82 Z" fill={band} stroke={INK} strokeWidth="2.4" strokeLinejoin="round" />
    <path d="M29 88 L31 30 Q50 25 69 30 L71 88" fill="none" {...S} />
    {starsOn && [[40, 42], [58, 38], [50, 56], [62, 58], [38, 60]].map(([x, y], i) => <path key={i} d={starPath(x, y, 4)} fill={starsOn} />)}
    {emblem && <Put m={emblem} x={50} y={76} s={1.3} />}
    <path d="M36 36 Q36 54 37 64" stroke="#fff" strokeWidth="3" opacity="0.25" fill="none" strokeLinecap="round" />
  </g>
);

const Wreath = ({ items, leaf = "#3d9b4a", band = "#6b4a2b", s = 2.2 }) => {
  const p0 = [12, 96];
  const p1 = [50, 62];
  const p2 = [88, 96];
  return (
    <g>
      <path d="M12 96 Q50 62 88 96" stroke={INK} strokeWidth="8" fill="none" strokeLinecap="round" />
      <path d="M12 96 Q50 62 88 96" stroke={band} strokeWidth="4.5" fill="none" strokeLinecap="round" />
      {Array.from({ length: 9 }, (_, i) => {
        const [x, y] = qpt(p0, p1, p2, 0.06 + (i / 8) * 0.88);
        return <ellipse key={`l${i}`} cx={x} cy={y - 3} rx="5" ry="8" fill={leaf} stroke={INK} strokeWidth="1.6" transform={`rotate(${(i % 2 ? 55 : -55) + (x - 50) * 0.4} ${x} ${y})`} />;
      })}
      {items.map((m, i) => {
        const [x, y] = qpt(p0, p1, p2, 0.12 + (i / Math.max(1, items.length - 1)) * 0.76);
        return <Put key={i} m={m} x={x} y={y - 6} s={s} r={(x - 50) * 0.35} />;
      })}
    </g>
  );
};

const BucketHat = ({ cols, top }) => (
  <g>
    <defs>
      <clipPath id="sbl-ss-bucket">
        <path d="M24 86 Q24 44 50 44 Q76 44 76 86 Z" />
      </clipPath>
    </defs>
    <path d="M24 86 Q24 44 50 44 Q76 44 76 86 Z" fill={top || cols[0]} />
    <g clipPath="url(#sbl-ss-bucket)">
      {cols.map((c, i) => (
        <rect key={i} x="0" y={58 + i * 7} width="100" height="7" fill={c} />
      ))}
    </g>
    <path d="M24 86 Q24 44 50 44 Q76 44 76 86 Z" fill="none" {...S} />
    <path d="M8 92 Q50 74 92 92 L86 99 Q50 86 14 99 Z" fill={shade(cols[0], 0.85)} {...S} />
  </g>
);

const EarsBand = ({ kind, c, inner, band = "#2a2130" }) => (
  <g>
    {kind === "bunny" && (
      <g {...S}>
        <ellipse cx="36" cy="40" rx="10" ry="30" fill={c} transform="rotate(-12 36 70)" />
        <ellipse cx="64" cy="40" rx="10" ry="30" fill={c} transform="rotate(12 64 70)" />
        <ellipse cx="36" cy="44" rx="4.5" ry="20" fill={inner} stroke="none" transform="rotate(-12 36 70)" />
        <ellipse cx="64" cy="44" rx="4.5" ry="20" fill={inner} stroke="none" transform="rotate(12 64 70)" />
      </g>
    )}
    <path d="M12 98 Q50 60 88 98" stroke={INK} strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M12 98 Q50 60 88 98" stroke={band} strokeWidth="5.5" fill="none" strokeLinecap="round" />
  </g>
);

// --- special hats ----------------------------------------------------------
const SantaHat = () => (
  <g {...S}>
    <path d="M20 86 C22 48 46 22 74 26 C88 28 94 44 90 58 C82 46 72 46 66 56 C62 66 68 78 74 86 Z" fill="#d8343b" />
    <path d="M34 54 Q44 38 58 34" stroke="#ff8a8a" strokeWidth="3" fill="none" />
    <rect x="14" y="78" width="72" height="20" rx="10" fill="#fff" />
    <path d="M24 84 q3 -3 6 0 M40 88 q3 -3 6 0 M58 84 q3 -3 6 0 M72 88 q3 -3 6 0" stroke="#dfe6ef" strokeWidth="2" fill="none" />
    <circle cx="90" cy="62" r="9.5" fill="#fff" />
  </g>
);
const ElfHat = () => (
  <g>
    <g {...S}>
      <path d="M22 88 C28 60 40 30 62 12 C62 40 70 64 78 88 Z" fill="#2fae5b" />
      <path d="M62 12 C70 6 80 8 84 16" fill="none" stroke="#2fae5b" strokeWidth="7" />
      <circle cx="86" cy="19" r="6.5" fill="#f2c230" />
    </g>
    <path d="M40 54 Q48 40 56 30" stroke="#7fe0a0" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6" />
    <path d="M14 86 L24 98 L34 86 L44 98 L54 86 L64 98 L74 86 L84 98 L86 84 L14 84 Z" fill="#d8343b" {...S} strokeWidth="2.4" />
    {[24, 44, 64, 84].map((x) => (
      <circle key={x} cx={x} cy="99" r="3.2" fill="#f2c230" stroke={INK} strokeWidth="1.4" />
    ))}
  </g>
);
const AntlerHalf = ({ flip }) => (
  <g transform={flip ? "translate(100 0) scale(-1 1)" : undefined}>
    <path d="M32 90 C28 70 24 56 18 44 M23 58 C15 54 9 48 8 38 M19 48 C23 40 25 32 24 22 M18 44 C12 36 12 28 14 18" stroke={INK} strokeWidth="8.5" fill="none" strokeLinecap="round" />
    <path d="M32 90 C28 70 24 56 18 44 M23 58 C15 54 9 48 8 38 M19 48 C23 40 25 32 24 22 M18 44 C12 36 12 28 14 18" stroke="#b07a46" strokeWidth="5" fill="none" strokeLinecap="round" />
  </g>
);
const AntlerBand = () => (
  <g>
    <AntlerHalf />
    <AntlerHalf flip />
    <path d="M12 98 Q50 60 88 98" stroke={INK} strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M12 98 Q50 60 88 98" stroke="#2fae5b" strokeWidth="5.5" fill="none" strokeLinecap="round" />
    <Put m={["christmas", "bell"]} x={50} y={80} s={1.4} />
    <Put m={["", "holly"]} x={31} y={86} s={1.3} r={-25} />
    <Put m={["", "holly"]} x={69} y={86} s={1.3} r={25} />
  </g>
);
const WitchHat = () => (
  <g>
    <g {...S}>
      <ellipse cx="50" cy="90" rx="44" ry="10" fill="#4a2c74" />
      <path d="M26 88 Q36 60 44 34 Q48 14 70 8 Q60 22 62 40 Q66 64 74 88 Z" fill="#6b3fa0" />
    </g>
    <path d="M29 78 Q50 84 71 78 L72.5 86 Q50 92 27.5 86 Z" fill="#7ed957" stroke={INK} strokeWidth="2.4" strokeLinejoin="round" />
    <rect x="44" y="76" width="12" height="11" rx="2" fill="#f2c230" stroke={INK} strokeWidth="2" />
    <rect x="47.5" y="79" width="5" height="5" fill="#7ed957" />
    <Put m={["halloween", "bat"]} x={56} y={52} s={1.5} r={-10} />
    <path d="M38 64 Q44 46 48 34" stroke="#9c74d6" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6" />
  </g>
);
const PumpkinHat = () => (
  <g {...S}>
    <path d="M50 40 Q50 26 60 22" stroke="#3f7a2a" strokeWidth="6" fill="none" />
    <path d="M58 30 Q72 22 76 32 Q66 36 58 30 Z" fill="#7ed957" strokeWidth="2.4" />
    <path d="M14 92 C10 54 34 38 50 40 C66 38 90 54 86 92 Z" fill="#ff8a1f" />
    <path d="M32 90 C28 64 38 46 50 42 M68 90 C72 64 62 46 50 42" stroke="#d96a00" strokeWidth="2.5" fill="none" />
    <path d="M28 70 l5 -7 5 7 Z M62 70 l5 -7 5 7 Z" fill={INK} strokeWidth="1" />
    <path d="M38 80 Q50 88 62 80 L58 84 L54 81 L50 85 L46 81 L42 84 Z" fill={INK} strokeWidth="1" />
  </g>
);
const TurkeyHat = () => (
  <g>
    {["#e5383b", "#ff8a1f", "#f2c230", "#7fb03a", "#f2c230", "#ff8a1f", "#e5383b"].map((c, i) => (
      <ellipse key={i} cx="50" cy="38" rx="8" ry="24" fill={c} stroke={INK} strokeWidth="2.2" transform={`rotate(${-66 + i * 22} 50 74)`} />
    ))}
    <g {...S}>
      <path d="M18 94 C16 60 84 60 82 94 Z" fill="#8a4b22" />
      <circle cx="50" cy="56" r="13" fill="#a0602e" />
    </g>
    <circle cx="45" cy="53" r="2.2" fill={INK} />
    <circle cx="55" cy="53" r="2.2" fill={INK} />
    <path d="M46 59 L54 59 L50 65 Z" fill="#f2c230" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M52 62 q4 6 -1 9" stroke="#e5383b" strokeWidth="4" fill="none" strokeLinecap="round" />
    <path d="M28 84 Q50 78 72 84" stroke="#c07a3a" strokeWidth="3" fill="none" />
  </g>
);
const ChickEggHat = () => (
  <g>
    <g {...S}>
      <circle cx="50" cy="44" r="18" fill="#ffe14d" />
      <path d="M44 26 q3 -6 6 0 q3 -6 6 0" fill="none" strokeWidth="2.4" />
    </g>
    <circle cx="44" cy="42" r="2.4" fill={INK} />
    <circle cx="56" cy="42" r="2.4" fill={INK} />
    <path d="M46 48 L54 48 L50 53 Z" fill="#ff9a3c" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
    <ellipse cx="38" cy="48" rx="3" ry="1.8" fill="#ffab7a" />
    <ellipse cx="62" cy="48" rx="3" ry="1.8" fill="#ffab7a" />
    <path d="M14 92 C12 70 18 58 24 56 L32 64 L40 54 L50 62 L60 54 L68 64 L76 56 C82 58 88 70 86 92 Z" fill="#fffaf0" {...S} />
    {[[30, 80, "#b69cf2"], [50, 84, "#7fd8be"], [70, 80, "#ff9ec4"]].map(([x, y, c]) => (
      <circle key={x} cx={x} cy={y} r="4" fill={c} />
    ))}
  </g>
);
const DragonHorns = () => (
  <g>
    <g {...S}>
      <path d="M30 80 C26 60 22 46 26 30 C34 44 38 60 40 78 Z" fill="#f6c343" />
      <path d="M70 80 C74 60 78 46 74 30 C66 44 62 60 60 78 Z" fill="#f6c343" />
    </g>
    <path d="M28 44 l8 2 M30 56 l8 1 M72 44 l-8 2 M70 56 l-8 1" stroke="#d29a00" strokeWidth="2" />
    {Array.from({ length: 9 }, (_, i) => {
      const [x, y] = qpt([14, 96], [50, 64], [86, 96], 0.04 + (i / 8) * 0.92);
      return <circle key={i} cx={x} cy={y - 3} r="7" fill={i % 2 ? "#ff8c42" : "#e03131"} stroke={INK} strokeWidth="1.8" />;
    })}
    <path d="M12 98 Q50 60 88 98" stroke={INK} strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M12 98 Q50 60 88 98" stroke="#e03131" strokeWidth="5.5" fill="none" strokeLinecap="round" />
    <circle cx="50" cy="79" r="4.5" fill="#f6c343" stroke={INK} strokeWidth="1.8" />
  </g>
);
const Beret = ({ c, m }) => (
  <g>
    <g {...S}>
      <path d="M50 30 l0 -8" strokeWidth="4" />
      <path d="M10 76 C6 50 40 32 66 36 C88 40 96 58 88 74 C76 88 30 90 10 76 Z" fill={c} />
      <path d="M18 80 Q50 92 82 80 L82 88 Q50 100 18 88 Z" fill={shade(c, 0.8)} />
    </g>
    <path d="M28 52 Q44 42 60 42" stroke="#fff" strokeWidth="3" opacity="0.35" fill="none" strokeLinecap="round" />
    {m && <Put m={m} x={70} y={62} s={2} r={12} />}
  </g>
);
const PeacockBand = () => (
  <g>
    {[-30, 0, 30].map((a) => (
      <g key={a} transform={`rotate(${a} 50 84)`}>
        <path d="M50 84 L50 22" stroke="#2a9d8f" strokeWidth="2.4" />
        <ellipse cx="50" cy="26" rx="10" ry="14" fill="#2ec4b6" stroke={INK} strokeWidth="2" />
        <ellipse cx="50" cy="28" rx="6" ry="8.5" fill="#f6c343" />
        <ellipse cx="50" cy="29" rx="4" ry="5.5" fill="#1d4fb8" />
        <ellipse cx="50" cy="30" rx="2" ry="2.8" fill="#14213d" />
      </g>
    ))}
    <path d="M12 98 Q50 60 88 98" stroke={INK} strokeWidth="9" fill="none" strokeLinecap="round" />
    <path d="M12 98 Q50 60 88 98" stroke="#f6c343" strokeWidth="5.5" fill="none" strokeLinecap="round" />
    {[0.3, 0.5, 0.7].map((t) => {
      const [x, y] = qpt([12, 98], [50, 60], [88, 98], t);
      return <circle key={t} cx={x} cy={y} r="3" fill="#d6246e" stroke={INK} strokeWidth="1.4" />;
    })}
  </g>
);

const HATS = {
  "ss-newyear-top-hat": () => <TopHat c="#1f1d2b" band="#f2c230" emblem={["", "sparkle"]} starsOn="#f2c230" />,
  "ss-newyear-party-cone": () => <ConeHat c="#f2c230" c2="#ff5fa2" tip="#4fd1ff" m={["newyear", "firework"]} stripes={false} />,
  "ss-newyear-star-boppers": () => <Boppers band="#f2c230" m={["", "star"]} m2={["newyear", "firework"]} />,
  "ss-lunarnewyear-lantern-boppers": () => <Boppers band="#e03131" m={["lunarnewyear", "lantern"]} />,
  "ss-lunarnewyear-red-knit": () => <Beanie c="#e03131" cuff="#f6c343" pom="#f6c343" emblem={["", "blossom"]} />,
  "ss-lunarnewyear-dragon-horns": DragonHorns,
  "ss-valentines-heart-boppers": () => <Boppers band="#ff8cc6" m={["valentines", "heart"]} />,
  "ss-valentines-heart-beret": () => <Beret c="#ff8cc6" m={["valentines", "heart"]} />,
  "ss-valentines-rose-crown": () => <Wreath items={[["valentines", "rose"], ["valentines", "heart"], ["valentines", "rose"], ["valentines", "heart"], ["valentines", "rose"]]} leaf="#3d9b4a" band="#ff8cc6" />,
  "ss-stpatricks-green-top-hat": () => <TopHat c="#1f9e4a" band="#26232e" emblem={["stpatricks", "shamrock"]} />,
  "ss-stpatricks-shamrock-boppers": () => <Boppers band="#1f9e4a" m={["stpatricks", "shamrock"]} m2={["stpatricks", "coin"]} />,
  "ss-stpatricks-clover-beanie": () => <Beanie c="#2fbf5b" cuff="#1f7a3a" pom="#f2c230" emblem={["stpatricks", "shamrock"]} />,
  "ss-easter-bunny-ears": () => <EarsBand kind="bunny" c="#fff" inner="#ffb3c8" band="#b69cf2" />,
  "ss-easter-flower-crown": () => <Wreath items={[["easter", "tulip"], ["", "blossom"], ["easter", "tulip"], ["", "blossom"], ["easter", "tulip"]]} leaf="#7fd36b" band="#7fd8be" />,
  "ss-easter-chick-egg": ChickEggHat,
  "ss-july4-star-top-hat": () => <TopHat c="#ffffff" stripes="#e23b4a" band="#1f3f8f" emblem={["july4", "star"]} />,
  "ss-july4-star-boppers": () => <Boppers band="#1f3f8f" m={["july4", "star"]} m2={["july4", "firework"]} />,
  "ss-july4-bucket-hat": () => <BucketHat cols={["#1f3f8f", "#ffffff", "#e23b4a", "#ffffff"]} top="#1f3f8f" />,
  "ss-halloween-witch-hat": WitchHat,
  "ss-halloween-pumpkin-hat": PumpkinHat,
  "ss-halloween-bat-boppers": () => <Boppers band="#ff8a1f" m={["halloween", "bat"]} m2={["halloween", "ghost"]} />,
  "ss-diwali-marigold-crown": () => <Wreath items={[["diwali", "marigold"], ["diwali", "marigold"], ["diwali", "marigold"], ["diwali", "marigold"], ["diwali", "marigold"]]} leaf="#3d9b4a" band="#f6c343" s={2} />,
  "ss-diwali-diya-boppers": () => <Boppers band="#d6246e" m={["diwali", "diya"]} />,
  "ss-diwali-peacock-band": PeacockBand,
  "ss-thanksgiving-turkey-hat": TurkeyHat,
  "ss-thanksgiving-leaf-crown": () => <Wreath items={[["thanksgiving", "leaf"], ["thanksgiving", "acorn"], ["thanksgiving", "leaf"], ["thanksgiving", "acorn"], ["thanksgiving", "leaf"]]} leaf="#c8641e" band="#8a4b22" />,
  "ss-thanksgiving-acorn-beanie": () => <Beanie c="#e07b2a" cuff="#8a4b22" pom="#f2b134" emblem={["thanksgiving", "acorn"]} />,
  "ss-hanukkah-blue-beanie": () => <Beanie c="#2a5bd7" cuff="#e6efff" stripe="#ffffff" pom="#c9d3e6" emblem={["hanukkah", "dreidel"]} />,
  "ss-hanukkah-dreidel-boppers": () => <Boppers band="#2a5bd7" m={["hanukkah", "dreidel"]} m2={["hanukkah", "gelt"]} />,
  "ss-hanukkah-party-cone": () => <ConeHat c="#2a5bd7" c2="#c9d3e6" tip="#f2c230" m={["hanukkah", "gelt"]} />,
  "ss-christmas-santa-hat": SantaHat,
  "ss-christmas-elf-hat": ElfHat,
  "ss-christmas-antlers": AntlerBand,
  "ss-kwanzaa-harvest-crown": () => <Wreath items={[["kwanzaa", "corn"], ["kwanzaa", "basket"], ["kwanzaa", "corn"]]} leaf="#1f8a3e" band="#c62f2f" s={2.4} />,
  "ss-kwanzaa-stripe-beanie": () => <Beanie c="#c62f2f" cuff="#1f8a3e" stripe="#2a2420" pom="#f2c230" dots="#f2c230" />,
  "ss-kwanzaa-bucket-hat": () => <BucketHat cols={["#2a2420", "#c62f2f", "#f2c230", "#1f8a3e"]} top="#2a2420" />,
};

// ===========================================================================
// WINGS — behind the avatar, body at (50,50)
// ===========================================================================

const FeatherWings = ({ cols, tip }) => {
  const rows = [
    "M50 42 Q30 16 4 20 Q12 28 8 33 Q30 40 50 50 Z",
    "M50 48 Q28 34 8 38 Q14 46 10 50 Q30 54 50 56 Z",
    "M50 54 Q32 50 14 56 Q20 62 18 66 Q36 64 50 60 Z",
  ];
  const half = (
    <g>
      {rows.map((d, i) => (
        <path key={i} d={d} fill={cols[i % cols.length]} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      ))}
    </g>
  );
  return (
    <g>
      {half}
      <g transform="translate(100 0) scale(-1 1)">{half}</g>
      {tip &&
        [
          [8, 22],
          [11, 40],
          [18, 58],
          [92, 22],
          [89, 40],
          [82, 58],
        ].map(([x, y], i) => <Put key={i} m={tip} x={x} y={y} s={0.9} />)}
    </g>
  );
};

const ButterflyW = ({ top, bottom, spots, ring = "#fff" }) => (
  <g>
    <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
      <path d="M50 46 Q30 6 6 16 Q-4 36 20 50 Z" fill={top} />
      <path d="M50 46 Q70 6 94 16 Q104 36 80 50 Z" fill={top} />
      <path d="M50 50 Q20 52 12 72 Q20 88 50 56 Z" fill={bottom} />
      <path d="M50 50 Q80 52 88 72 Q80 88 50 56 Z" fill={bottom} />
    </g>
    <path d="M12 22 Q26 18 40 34 M88 22 Q74 18 60 34" stroke={ring} strokeWidth="2" fill="none" opacity="0.7" />
    {spots &&
      [
        [22, 28, 1.3],
        [78, 28, 1.3],
        [24, 66, 0.9],
        [76, 66, 0.9],
      ].map(([x, y, s], i) => <Put key={i} m={spots} x={x} y={y} s={s} />)}
  </g>
);

const BatW = ({ c, c2 }) => {
  const half = "M50 44 Q34 22 6 26 Q10 34 6 42 Q14 40 16 48 Q22 44 26 52 Q32 46 38 54 Q44 48 50 54 Z";
  return (
    <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
      <path d={half} fill={c} />
      <path d={half} fill={c} transform="translate(100 0) scale(-1 1)" />
      <path d="M44 46 L16 30 M44 48 L18 46 M44 50 L30 50 M56 46 L84 30 M56 48 L82 46 M56 50 L70 50" stroke={c2} strokeWidth="1.6" fill="none" />
    </g>
  );
};

const LeafW = ({ c, vein, extra }) => {
  const half = (
    <g>
      <path d="M50 46 Q30 8 4 14 Q6 42 46 52 Z" fill={c} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <path d="M50 52 Q20 50 10 72 Q34 80 50 56 Z" fill={shade(c, 0.88)} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <path d="M48 48 Q26 30 8 16 M30 32 L20 32 M36 38 L30 28 M48 54 Q30 60 14 70" stroke={vein} strokeWidth="1.6" fill="none" />
    </g>
  );
  return (
    <g>
      {half}
      <g transform="translate(100 0) scale(-1 1)">{half}</g>
      {extra &&
        [
          [14, 22],
          [86, 22],
          [18, 68],
          [82, 68],
        ].map(([x, y], i) => <Put key={i} m={extra} x={x} y={y} s={1.1} />)}
    </g>
  );
};

const FanW = ({ cols, edge = "#f6c343", m }) => {
  const R0 = 48;
  const a0 = 160;
  const a1 = 262;
  const pt = (deg, r = R0) => [48 + Math.cos((deg * Math.PI) / 180) * r, 54 + Math.sin((deg * Math.PI) / 180) * r];
  const [sx, sy] = pt(a0);
  const [ex, ey] = pt(a1);
  const outline = `M48 54 L${sx.toFixed(1)} ${sy.toFixed(1)} A${R0} ${R0} 0 0 1 ${ex.toFixed(1)} ${ey.toFixed(1)} Z`;
  const pleats = 8;
  const half = (
    <g>
      <path d={outline} fill={cols[0]} />
      {Array.from({ length: pleats }, (_, k) => {
        const d0 = a0 + ((a1 - a0) * k) / pleats;
        const d1 = a0 + ((a1 - a0) * (k + 1)) / pleats;
        const [x0, y0] = pt(d0);
        const [x1, y1] = pt(d1);
        return k % 2 ? <path key={k} d={`M48 54 L${x0.toFixed(1)} ${y0.toFixed(1)} A${R0} ${R0} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)} Z`} fill={cols[1]} /> : null;
      })}
      <path d={`M${sx.toFixed(1)} ${sy.toFixed(1)} A${R0} ${R0} 0 0 1 ${ex.toFixed(1)} ${ey.toFixed(1)}`} stroke={edge} strokeWidth="5" fill="none" />
      <path d={outline} fill="none" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
    </g>
  );
  return (
    <g>
      {half}
      <g transform="translate(100 0) scale(-1 1)">{half}</g>
      {m && <Put m={m} x={18} y={30} s={1.3} />}
      {m && <Put m={m} x={82} y={30} s={1.3} />}
    </g>
  );
};

const FairyW = ({ c, edge, m }) => (
  <g>
    <g stroke={edge} strokeWidth="2" strokeLinejoin="round" opacity="0.95">
      <path d="M50 46 Q34 2 12 8 Q2 26 46 52 Z" fill={c} fillOpacity="0.7" />
      <path d="M50 46 Q66 2 88 8 Q98 26 54 52 Z" fill={c} fillOpacity="0.7" />
      <path d="M48 52 Q24 56 18 76 Q32 82 50 56 Z" fill={c} fillOpacity="0.6" />
      <path d="M52 52 Q76 56 82 76 Q68 82 50 56 Z" fill={c} fillOpacity="0.6" />
    </g>
    <path d="M46 48 Q28 26 16 12 M54 48 Q72 26 84 12" stroke="#fff" strokeWidth="1.4" fill="none" opacity="0.8" />
    {m &&
      [
        [18, 18, 1.1],
        [82, 18, 1.1],
        [30, 34, 0.7],
        [70, 34, 0.7],
        [22, 70, 0.8],
        [78, 70, 0.8],
      ].map(([x, y, s], i) => <Put key={i} m={m} x={x} y={y} s={s} />)}
  </g>
);

const PeacockW = () => {
  const half = (
    <g>
      {[0, 1, 2, 3, 4].map((k) => {
        const a = (195 + k * 16) * (Math.PI / 180);
        const x = 50 + Math.cos(a) * 40;
        const y = 50 + Math.sin(a) * 40;
        const deg = (a * 180) / Math.PI + 90;
        return (
          <g key={k}>
            <path d={`M50 52 L${x.toFixed(1)} ${y.toFixed(1)}`} stroke="#2a9d8f" strokeWidth="2" />
            <ellipse cx={x} cy={y} rx="8" ry="12" fill="#2ec4b6" stroke={INK} strokeWidth="1.8" transform={`rotate(${deg} ${x} ${y})`} />
            <ellipse cx={x} cy={y} rx="5" ry="7" fill="#f6c343" transform={`rotate(${deg} ${x} ${y})`} />
            <ellipse cx={x} cy={y} rx="3.2" ry="4.4" fill="#1d4fb8" transform={`rotate(${deg} ${x} ${y})`} />
            <circle cx={x} cy={y} r="1.6" fill="#14213d" />
          </g>
        );
      })}
    </g>
  );
  return (
    <g>
      {half}
      <g transform="translate(100 0) scale(-1 1)">{half}</g>
    </g>
  );
};

const WINGS = {
  "ss-newyear-gold-wings": () => <FeatherWings cols={["#f2c230", "#fff3c4", "#e0a800"]} tip={["", "sparkle"]} />,
  "ss-newyear-firework-wings": () => <ButterflyW top="#1f2a5c" bottom="#33407a" spots={["newyear", "firework"]} ring="#f2c230" />,
  "ss-lunarnewyear-fan-wings": () => <FanW cols={["#e03131", "#ff8c42"]} m={["", "blossom"]} />,
  "ss-lunarnewyear-blossom-wings": () => <ButterflyW top="#e03131" bottom="#f6c343" spots={["", "blossom"]} />,
  "ss-valentines-heart-wings": () => <FeatherWings cols={["#ff8cc6", "#ffe4ec", "#e83f6f"]} tip={["", "heart"]} />,
  "ss-valentines-sweet-wings": () => <ButterflyW top="#ffb3c8" bottom="#e83f6f" spots={["valentines", "heart"]} />,
  "ss-stpatricks-clover-wings": () => <LeafW c="#2fbf5b" vein="#1f7a3a" extra={["stpatricks", "shamrock"]} />,
  "ss-stpatricks-lucky-wings": () => <FairyW c="#9be8a8" edge="#1f9e4a" m={["stpatricks", "coin"]} />,
  "ss-easter-pastel-wings": () => <ButterflyW top="#b69cf2" bottom="#7fd8be" spots={["easter", "egg"]} />,
  "ss-easter-chick-wings": () => <FeatherWings cols={["#ffe14d", "#fff3a8", "#ffd23f"]} tip={["", "blossom"]} />,
  "ss-july4-star-wings": () => <FeatherWings cols={["#1f3f8f", "#ffffff", "#e23b4a"]} tip={["", "star"]} />,
  "ss-july4-sparkle-wings": () => <FairyW c="#9fb8ff" edge="#1f3f8f" m={["july4", "firework"]} />,
  "ss-halloween-bat-wings": () => <BatW c="#5b3a8c" c2="#8e6ac4" />,
  "ss-halloween-spooky-wings": () => <FairyW c="#c9b2ef" edge="#6b3fa0" m={["halloween", "ghost"]} />,
  "ss-diwali-peacock-wings": PeacockW,
  "ss-diwali-festival-wings": () => <FanW cols={["#ff9f1c", "#d6246e"]} edge="#f6c343" m={["diwali", "rangoli"]} />,
  "ss-thanksgiving-turkey-wings": () => <FeatherWings cols={["#e5383b", "#f2b134", "#8a4b22"]} />,
  "ss-thanksgiving-leaf-wings": () => <LeafW c="#e8601c" vein="#a8380c" extra={["thanksgiving", "acorn"]} />,
  "ss-hanukkah-silver-wings": () => <FeatherWings cols={["#c9d3e6", "#ffffff", "#2a5bd7"]} tip={["", "sparkle"]} />,
  "ss-hanukkah-blue-wings": () => <ButterflyW top="#2a5bd7" bottom="#c9d3e6" spots={["hanukkah", "dreidel"]} />,
  "ss-christmas-snowflake-wings": () => <FairyW c="#cfeaff" edge="#5aa2ff" m={["", "snowflake"]} />,
  "ss-christmas-holly-wings": () => <LeafW c="#2fae5b" vein="#1d6e38" extra={["christmas", "ornament"]} />,
  "ss-kwanzaa-harvest-wings": () => <FeatherWings cols={["#c62f2f", "#2a2420", "#1f8a3e"]} />,
  "ss-kwanzaa-gold-wings": () => <ButterflyW top="#f2c230" bottom="#c62f2f" spots={["kwanzaa", "corn"]} ring="#1f8a3e" />,
};

// ===========================================================================
// PETS — a critter kit (fur animals) + a bird kit + a few specials
// ===========================================================================

const Eyes = ({ y = 46, big = false }) => (
  <g>
    <circle cx="41" cy={y} r={big ? 4.6 : 3.8} fill={INK} />
    <circle cx="59" cy={y} r={big ? 4.6 : 3.8} fill={INK} />
    <circle cx="42.4" cy={y - 1.5} r="1.4" fill="#fff" />
    <circle cx="60.4" cy={y - 1.5} r="1.4" fill="#fff" />
  </g>
);
const Cheeks = ({ y = 55 }) => (
  <g fill="#ff8fa3" opacity="0.5">
    <ellipse cx="34" cy={y} rx="4.4" ry="2.6" />
    <ellipse cx="66" cy={y} rx="4.4" ry="2.6" />
  </g>
);

// neck / body accessories
const Scarf = ({ c, c2 }) => (
  <g stroke={INK} strokeWidth="2.2" strokeLinejoin="round">
    <path d="M27 64 Q50 78 73 64 L74 72 Q50 86 26 72 Z" fill={c} />
    <path d="M60 74 L67 94 L57 92 Z" fill={c} />
    {c2 && <path d="M38 71 L38 79 M50 74 L50 82 M62 71 L62 79 M60 84 L65 84" stroke={c2} strokeWidth="2.4" />}
  </g>
);
const BowTie = ({ c, y = 70 }) => (
  <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
    <path d={`M50 ${y} L38 ${y - 6} L38 ${y + 6} Z M50 ${y} L62 ${y - 6} L62 ${y + 6} Z`} fill={c} />
    <circle cx="50" cy={y} r="3.2" fill={shade(c, 0.85)} />
  </g>
);
const Bow = ({ c, x = 64, y = 24 }) => (
  <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
    <path d={`M${x} ${y} L${x - 11} ${y - 7} L${x - 11} ${y + 7} Z M${x} ${y} L${x + 11} ${y - 7} L${x + 11} ${y + 7} Z`} fill={c} />
    <circle cx={x} cy={y} r="3.4" fill={shade(c, 0.85)} />
  </g>
);
const Collar = ({ c, tag }) => (
  <g>
    <path d="M28 66 Q50 78 72 66" stroke={INK} strokeWidth="7.5" fill="none" strokeLinecap="round" />
    <path d="M28 66 Q50 78 72 66" stroke={c} strokeWidth="4.5" fill="none" strokeLinecap="round" />
    {tag && <Put m={tag} x={50} y={78} s={0.95} />}
  </g>
);
const Bandana = ({ c, m }) => (
  <g>
    <path d="M28 64 Q50 74 72 64 L50 90 Z" fill={c} stroke={INK} strokeWidth="2.2" strokeLinejoin="round" />
    {m && <Put m={m} x={50} y={74} s={0.8} />}
  </g>
);
const Holding = ({ m, s = 1.6 }) => (
  <g>
    <Put m={m} x={50} y={80} s={s} />
    <ellipse cx="40" cy="84" rx="5" ry="4" fill="#fff" stroke={INK} strokeWidth="1.8" opacity="0" />
  </g>
);
const MiniHat = ({ kind, c = "#d8343b", c2 = "#fff" }) => {
  if (kind === "cone")
    return (
      <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
        <path d="M60 4 L72 26 L50 28 Z" fill={c} transform="rotate(14 60 20)" />
        <circle cx="62" cy="4" r="3.6" fill={c2} />
      </g>
    );
  if (kind === "santa")
    return (
      <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
        <path d="M34 24 C36 8 58 2 70 10 C76 14 78 20 76 24 Z" fill="#d8343b" />
        <rect x="30" y="20" width="44" height="8" rx="4" fill="#fff" />
        <circle cx="76" cy="12" r="4.4" fill="#fff" />
      </g>
    );
  if (kind === "top")
    return (
      <g stroke={INK} strokeWidth="2" strokeLinejoin="round">
        <ellipse cx="58" cy="24" rx="15" ry="4" fill={shade(c, 0.85)} />
        <path d="M49 23 L50 6 Q58 4 66 6 L67 23 Z" fill={c} />
        <rect x="49.5" y="16" width="17" height="4.5" fill={c2} />
      </g>
    );
  return null;
};

const ears = (kind, fur, inner) => {
  switch (kind) {
    case "round":
      return (
        <g {...S}>
          <circle cx="29" cy="27" r="9.5" fill={fur} />
          <circle cx="71" cy="27" r="9.5" fill={fur} />
          <circle cx="29" cy="27" r="4.6" fill={inner} stroke="none" />
          <circle cx="71" cy="27" r="4.6" fill={inner} stroke="none" />
        </g>
      );
    case "pointy":
      return (
        <g {...S}>
          <path d="M26 38 L28 8 L46 24 Z M74 38 L72 8 L54 24 Z" fill={fur} />
          <path d="M30 28 L31 16 L39 23 Z M70 28 L69 16 L61 23 Z" fill={inner} strokeWidth="0" />
        </g>
      );
    case "bunny":
      return (
        <g {...S}>
          <ellipse cx="38" cy="14" rx="7" ry="18" fill={fur} transform="rotate(-12 38 30)" />
          <ellipse cx="62" cy="14" rx="7" ry="18" fill={fur} transform="rotate(12 62 30)" />
          <ellipse cx="38" cy="16" rx="3" ry="12" fill={inner} stroke="none" transform="rotate(-12 38 30)" />
          <ellipse cx="62" cy="16" rx="3" ry="12" fill={inner} stroke="none" transform="rotate(12 62 30)" />
        </g>
      );
    case "tiny":
      return (
        <g {...S}>
          <circle cx="32" cy="26" r="5.6" fill={fur} />
          <circle cx="68" cy="26" r="5.6" fill={fur} />
        </g>
      );
    case "side":
      return (
        <g {...S}>
          <ellipse cx="25" cy="38" rx="9" ry="5" fill={fur} transform="rotate(-25 25 38)" />
          <ellipse cx="75" cy="38" rx="9" ry="5" fill={fur} transform="rotate(25 75 38)" />
        </g>
      );
    default:
      return null;
  }
};

/** a round furry critter: body, head, ears, face + extras in front */
const Critter = ({ fur, belly, head, ear = "round", earIn = "#ffb3c8", snout, nose = INK, floppy, back, front, whiskers, face = true, headR = 25 }) => (
  <g>
    {back}
    <g {...S}>
      <ellipse cx="50" cy="80" rx="27" ry="16" fill={fur} />
    </g>
    {belly && <ellipse cx="50" cy="85" rx="15" ry="8.5" fill={belly} stroke={INK} strokeWidth="2" />}
    {ears(ear, head || fur, earIn)}
    <circle cx="50" cy="46" r={headR} fill={head || fur} {...S} />
    {floppy && (
      <g {...S}>
        <path d="M27 32 Q14 44 20 66 Q30 60 32 44 Z M73 32 Q86 44 80 66 Q70 60 68 44 Z" fill={floppy} />
      </g>
    )}
    {snout && <ellipse cx="50" cy="57" rx="11" ry="8" fill={snout} stroke={INK} strokeWidth="2" />}
    {face && (
      <g>
        <Eyes />
        <ellipse cx="50" cy="54" rx="4" ry="3" fill={nose} />
        <path d="M44.5 59.5 Q50 64 55.5 59.5" stroke={INK} strokeWidth="2" fill="none" strokeLinecap="round" />
        <Cheeks />
      </g>
    )}
    {whiskers && <path d="M28 54 L16 52 M28 58 L17 61 M72 54 L84 52 M72 58 L83 61" stroke={INK} strokeWidth="1.6" />}
    {front}
  </g>
);

/** a round bird */
const Bird = ({ body, belly, face, beak = "#ff9a3c", back, front, tufts, wing, bigEyes, feet = "#ff9a3c", hood }) => (
  <g>
    {back}
    {feet && (
      <g fill={feet} stroke={INK} strokeWidth="1.8" strokeLinejoin="round">
        <path d="M38 88 l-4 8 h10 Z M62 88 l-4 8 h10 Z" />
      </g>
    )}
    {tufts && <path d="M28 30 L24 12 L40 24 Z M72 30 L76 12 L60 24 Z" fill={body} {...S} />}
    <path d="M50 22 C72 22 80 44 79 62 C78 82 66 92 50 92 C34 92 22 82 21 62 C20 44 28 22 50 22 Z" fill={body} {...S} />
    {hood && <path d="M22 54 C22 34 32 22 50 22 C68 22 78 34 78 54 Q50 62 22 54 Z" fill={hood} stroke={INK} strokeWidth="2.4" />}
    {belly && <ellipse cx="50" cy="70" rx="17" ry="18" fill={belly} />}
    {face && <ellipse cx="50" cy="46" rx="22" ry="15" fill={face} />}
    <g stroke={INK} strokeWidth="2.2" strokeLinejoin="round">
      <path d="M22 58 Q12 70 22 80 Q28 72 26 60 Z" fill={wing || shade(body, 0.85)} />
      <path d="M78 58 Q88 70 78 80 Q72 72 74 60 Z" fill={wing || shade(body, 0.85)} />
    </g>
    {bigEyes ? (
      <g>
        <circle cx="40" cy="44" r="8" fill="#fff" stroke={INK} strokeWidth="2" />
        <circle cx="60" cy="44" r="8" fill="#fff" stroke={INK} strokeWidth="2" />
        <circle cx="41" cy="45" r="4.4" fill={INK} />
        <circle cx="59" cy="45" r="4.4" fill={INK} />
        <circle cx="42.4" cy="43.4" r="1.5" fill="#fff" />
        <circle cx="60.4" cy="43.4" r="1.5" fill="#fff" />
      </g>
    ) : (
      <Eyes y={45} />
    )}
    <path d="M45 52 L55 52 L50 60 Z" fill={beak} stroke={INK} strokeWidth="1.8" strokeLinejoin="round" />
    <Cheeks y={54} />
    {front}
  </g>
);

const Snowman = () => (
  <g>
    <g {...S}>
      <circle cx="50" cy="76" r="20" fill="#fff" />
      <circle cx="50" cy="42" r="19" fill="#fff" />
      <path d="M30 70 L14 60 M18 63 L14 56 M70 70 L86 60 M82 63 L86 56" stroke="#7a4a24" strokeWidth="3" />
    </g>
    <path d="M31 40 C30 22 70 22 69 40 Z" fill="#2fae5b" stroke={INK} strokeWidth="2.4" strokeLinejoin="round" />
    <rect x="29" y="34" width="42" height="8" rx="4" fill="#d8343b" stroke={INK} strokeWidth="2.2" />
    <circle cx="50" cy="20" r="5" fill="#fff" stroke={INK} strokeWidth="2.2" />
    <circle cx="43" cy="47" r="2.6" fill={INK} />
    <circle cx="57" cy="47" r="2.6" fill={INK} />
    <path d="M50 52 L63 54 L50 56 Z" fill="#ff9a3c" stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M44 58 Q50 62 56 58" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <Cheeks y={55} />
    <Scarf c="#d8343b" c2="#fff" />
    {[76, 84].map((y) => (
      <circle key={y} cx="50" cy={y} r="2.4" fill={INK} />
    ))}
  </g>
);
const Ghost = () => (
  <g>
    <path d="M24 92 L24 46 Q24 18 50 18 Q76 18 76 46 L76 92 L67 84 L58 92 L50 84 L42 92 L33 84 Z" fill="#fff" {...S} />
    <path d="M24 60 Q12 58 14 50 M76 60 Q88 58 86 50" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
    <ellipse cx="41" cy="46" rx="4" ry="5.5" fill={INK} />
    <ellipse cx="59" cy="46" rx="4" ry="5.5" fill={INK} />
    <circle cx="42.3" cy="44" r="1.4" fill="#fff" />
    <circle cx="60.3" cy="44" r="1.4" fill="#fff" />
    <ellipse cx="50" cy="59" rx="4" ry="3" fill={INK} />
    <Cheeks y={54} />
  </g>
);
const Frog = ({ hat }) => (
  <g>
    <g {...S}>
      <ellipse cx="50" cy="80" rx="28" ry="15" fill="#5cc85a" />
      <circle cx="35" cy="30" r="11" fill="#5cc85a" />
      <circle cx="65" cy="30" r="11" fill="#5cc85a" />
      <ellipse cx="50" cy="50" rx="28" ry="21" fill="#5cc85a" />
    </g>
    <ellipse cx="50" cy="84" rx="15" ry="8" fill="#c6f2a6" />
    <circle cx="35" cy="30" r="6" fill="#fff" />
    <circle cx="65" cy="30" r="6" fill="#fff" />
    <circle cx="36" cy="31" r="3.4" fill={INK} />
    <circle cx="66" cy="31" r="3.4" fill={INK} />
    <path d="M36 55 Q50 66 64 55" stroke={INK} strokeWidth="2.4" fill="none" strokeLinecap="round" />
    <Cheeks y={52} />
    {hat}
  </g>
);
const Turtle = () => (
  <g>
    <g {...S}>
      <circle cx="80" cy="64" r="11" fill="#7fd36b" />
      <ellipse cx="34" cy="90" rx="7" ry="5" fill="#7fd36b" />
      <ellipse cx="66" cy="90" rx="7" ry="5" fill="#7fd36b" />
      <path d="M14 84 C14 50 30 38 50 38 C70 38 86 50 86 84 Z" fill="#1f3f8f" />
    </g>
    {[[34, 58], [50, 52], [66, 58], [42, 72], [58, 72]].map(([x, y], i) => (
      <path key={i} d={starPath(x, y, 6)} fill={i % 2 ? "#e23b4a" : "#fff"} stroke={INK} strokeWidth="1.4" strokeLinejoin="round" />
    ))}
    <path d="M12 84 H88" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    <circle cx="83" cy="62" r="2.2" fill={INK} />
    <path d="M80 69 q4 2 7 -1" stroke={INK} strokeWidth="1.6" fill="none" />
  </g>
);
const Elephant = () => (
  <g>
    <g {...S}>
      <ellipse cx="50" cy="80" rx="27" ry="16" fill="#9aa5b8" />
      <ellipse cx="22" cy="46" rx="15" ry="19" fill="#aab4c6" />
      <ellipse cx="78" cy="46" rx="15" ry="19" fill="#aab4c6" />
      <circle cx="50" cy="44" r="23" fill="#b8c2d4" />
      <path d="M44 56 Q42 74 50 84 Q56 86 58 80 Q52 74 56 56 Z" fill="#b8c2d4" />
    </g>
    <ellipse cx="22" cy="46" rx="8" ry="11" fill="#ffb3c8" />
    <ellipse cx="78" cy="46" rx="8" ry="11" fill="#ffb3c8" />
    <path d="M38 24 Q50 14 62 24 L56 32 Q50 28 44 32 Z" fill="#d6246e" stroke={INK} strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="50" cy="30" r="3" fill="#f6c343" stroke={INK} strokeWidth="1.2" />
    <Eyes y={42} />
    <Cheeks y={52} />
    <path d="M28 66 Q50 76 72 66" stroke="#f6c343" strokeWidth="4" fill="none" />
    {[34, 42, 50, 58, 66].map((x) => (
      <circle key={x} cx={x} cy={x === 50 ? 72 : 70} r="2.4" fill="#ff9f1c" stroke={INK} strokeWidth="1" />
    ))}
  </g>
);
const Lamb = () => (
  <g>
    <g {...S}>
      <ellipse cx="50" cy="80" rx="27" ry="16" fill="#fffaf0" />
    </g>
    {Array.from({ length: 11 }, (_, i) => {
      const a = (i / 11) * Math.PI * 2;
      return <circle key={i} cx={50 + Math.cos(a) * 24} cy={46 + Math.sin(a) * 23} r="8.5" fill="#fffaf0" stroke={INK} strokeWidth="2.2" />;
    })}
    <circle cx="50" cy="46" r="24" fill="#fffaf0" />
    <ellipse cx="22" cy="44" rx="9" ry="5" fill="#f3d3c2" stroke={INK} strokeWidth="2.2" transform="rotate(-20 22 44)" />
    <ellipse cx="78" cy="44" rx="9" ry="5" fill="#f3d3c2" stroke={INK} strokeWidth="2.2" transform="rotate(20 78 44)" />
    <ellipse cx="50" cy="52" rx="15" ry="16" fill="#f3d3c2" stroke={INK} strokeWidth="2.2" />
    <Eyes y={49} />
    <ellipse cx="50" cy="58" rx="3" ry="2.2" fill="#d9849b" />
    <path d="M46 62 Q50 65 54 62" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <Bow c="#b69cf2" x={50} y={70} />
  </g>
);
const Dragon = () => (
  <g>
    <g {...S}>
      <path d="M24 76 Q8 72 6 58 Q16 64 26 64 Z" fill="#e03131" />
      <ellipse cx="52" cy="80" rx="27" ry="15" fill="#e03131" />
    </g>
    <ellipse cx="52" cy="84" rx="15" ry="8" fill="#f6c343" stroke={INK} strokeWidth="2" />
    {Array.from({ length: 9 }, (_, i) => {
      const a = ((200 + i * 17.5) * Math.PI) / 180;
      return <circle key={i} cx={52 + Math.cos(a) * 26} cy={46 + Math.sin(a) * 25} r="7" fill={i % 2 ? "#f6c343" : "#ff8c42"} stroke={INK} strokeWidth="2" />;
    })}
    <g {...S}>
      <path d="M36 26 L30 8 L44 20 Z M68 26 L74 8 L60 20 Z" fill="#f6c343" />
      <circle cx="52" cy="46" r="24" fill="#ff4d4d" />
    </g>
    <ellipse cx="52" cy="58" rx="12" ry="8" fill="#ff8a8a" stroke={INK} strokeWidth="2" />
    <circle cx="42" cy="44" r="5.4" fill="#fff" stroke={INK} strokeWidth="1.6" />
    <circle cx="62" cy="44" r="5.4" fill="#fff" stroke={INK} strokeWidth="1.6" />
    <circle cx="43" cy="45" r="2.8" fill={INK} />
    <circle cx="63" cy="45" r="2.8" fill={INK} />
    <circle cx="47" cy="56" r="1.4" fill={INK} />
    <circle cx="57" cy="56" r="1.4" fill={INK} />
    <path d="M46 61 Q52 65 58 61" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M36 50 q-10 4 -16 -2 M68 50 q10 4 16 -2" stroke="#f6c343" strokeWidth="2.4" fill="none" strokeLinecap="round" />
  </g>
);
const Giraffe = () => (
  <g>
    <g {...S}>
      <ellipse cx="50" cy="82" rx="26" ry="14" fill="#f2c25a" />
      <path d="M40 80 L42 52 L58 52 L60 80 Z" fill="#f2c25a" />
      <path d="M38 24 L36 10 M62 24 L64 10" stroke="#a0602e" strokeWidth="4" />
      <circle cx="36" cy="9" r="4" fill="#a0602e" />
      <circle cx="64" cy="9" r="4" fill="#a0602e" />
      <ellipse cx="26" cy="32" rx="8" ry="4.6" fill="#f2c25a" transform="rotate(-20 26 32)" />
      <ellipse cx="74" cy="32" rx="8" ry="4.6" fill="#f2c25a" transform="rotate(20 74 32)" />
      <ellipse cx="50" cy="38" rx="20" ry="17" fill="#f2c25a" />
      <ellipse cx="50" cy="50" rx="13" ry="9" fill="#ffe0a3" />
    </g>
    {[[40, 28], [62, 30], [46, 66], [56, 74], [34, 84], [64, 86]].map(([x, y], i) => (
      <ellipse key={i} cx={x} cy={y} rx="4" ry="3" fill="#b8742e" />
    ))}
    <Eyes y={38} />
    <circle cx="46" cy="50" r="1.4" fill={INK} />
    <circle cx="54" cy="50" r="1.4" fill={INK} />
    <path d="M45 54 Q50 57 55 54" stroke={INK} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <Scarf c="#c62f2f" c2="#1f8a3e" />
  </g>
);

const Mane = ({ c }) => (
  <g>
    {Array.from({ length: 14 }, (_, i) => {
      const a = (i / 14) * Math.PI * 2;
      return <circle key={i} cx={50 + Math.cos(a) * 26} cy={47 + Math.sin(a) * 25} r="9" fill={c} stroke={INK} strokeWidth="2.2" />;
    })}
  </g>
);
const TigerStripes = () => (
  <path d="M44 22 l2 8 M50 21 l0 9 M56 22 l-2 8 M26 42 l8 2 M26 50 l7 0 M74 42 l-8 2 M74 50 l-7 0" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
);
const Spikes = ({ c }) => (
  <g>
    {Array.from({ length: 13 }, (_, i) => {
      const a = ((160 + i * 18) * Math.PI) / 180;
      const x = 50 + Math.cos(a) * 26;
      const y = 50 + Math.sin(a) * 26;
      const deg = (a * 180) / Math.PI + 90;
      return <path key={i} d={`M${x - 5} ${y} L${x} ${y - 13} L${x + 5} ${y} Z`} fill={c} stroke={INK} strokeWidth="2" strokeLinejoin="round" transform={`rotate(${deg} ${x} ${y})`} />;
    })}
  </g>
);
const BigTail = ({ c, c2 }) => (
  <g>
    <path d="M70 88 C96 90 98 60 86 44 C80 34 90 26 82 20 C70 26 78 40 76 52 C74 66 64 76 70 88 Z" fill={c} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
    {c2 && <path d="M82 20 C78 24 79 30 82 34 C86 28 88 24 82 20 Z" fill={c2} />}
  </g>
);
const Antlers = () => (
  <g>
    <path d="M34 26 L26 6 M28 12 L18 8 M30 18 L22 18 M66 26 L74 6 M72 12 L82 8 M70 18 L78 18" stroke={INK} strokeWidth="7" strokeLinecap="round" />
    <path d="M34 26 L26 6 M28 12 L18 8 M30 18 L22 18 M66 26 L74 6 M72 12 L82 8 M70 18 L78 18" stroke="#b07a46" strokeWidth="3.6" strokeLinecap="round" />
  </g>
);

const PETS = {
  "ss-newyear-owl": () => <Bird body="#8a5a36" face="#e8c49a" belly="#c99566" beak="#f2c230" tufts bigEyes front={<MiniHat kind="cone" c="#f2c230" c2="#ff5fa2" />} />,
  "ss-newyear-party-pup": () => <Critter fur="#f2c48d" head="#f2c48d" ear="none" floppy="#a8693a" front={<g><BowTie c="#f2c230" /><MiniHat kind="top" c="#1f1d2b" c2="#f2c230" /></g>} />,
  "ss-newyear-hamster": () => <Critter fur="#f0b878" head="#f6c896" belly="#fff3e0" ear="tiny" nose="#e0828f" front={<g><Put m={["newyear", "horn"]} x={50} y={78} s={1.6} r={-15} /><Put m={["", "confetti"]} x={22} y={20} s={1.4} /><Put m={["", "confetti"]} x={80} y={18} s={1.4} /></g>} />,
  "ss-lunarnewyear-dragon": Dragon,
  "ss-lunarnewyear-rabbit": () => <Critter fur="#fffaf0" ear="bunny" nose="#ff8cb0" front={<g><Bow c="#e03131" x={64} y={22} /><Holding m={["lunarnewyear", "mandarin"]} s={1.5} /></g>} />,
  "ss-lunarnewyear-tiger": () => <Critter fur="#ff9f43" head="#ffa94d" belly="#fff3e0" ear="round" earIn="#fff3e0" snout="#fff3e0" front={<g><TigerStripes /><Scarf c="#e03131" c2="#f6c343" /></g>} />,
  "ss-valentines-teddy": () => <Critter fur="#b97a46" head="#c98a56" ear="round" earIn="#e8c49a" snout="#e8c49a" front={<Holding m={["valentines", "heart"]} s={2} />} />,
  "ss-valentines-kitten": () => <Critter fur="#f4f1ec" head="#fffaf5" ear="pointy" nose="#ff7aa8" whiskers front={<Bow c="#e83f6f" x={66} y={22} />} />,
  "ss-valentines-lovebird": () => <Bird body="#ff8cc6" belly="#ffd1e6" wing="#7fd36b" beak="#ff9a3c" front={<Put m={["", "heart"]} x={76} y={20} s={1.4} r={15} />} />,
  "ss-stpatricks-frog": () => <Frog hat={<MiniHat kind="top" c="#1f9e4a" c2="#f2c230" />} />,
  "ss-stpatricks-pup": () => <Critter fur="#c0582b" head="#cf6a3a" ear="none" floppy="#9a3f1a" front={<Collar c="#1f9e4a" tag={["stpatricks", "shamrock"]} />} />,
  "ss-stpatricks-hedgehog": () => <Critter fur="#c9a27a" head="#f1d7b6" ear="tiny" back={<Spikes c="#8a6a4a" />} front={<Put m={["stpatricks", "shamrock"]} x={68} y={20} s={1.6} r={15} />} />,
  "ss-easter-bunny": () => <Critter fur="#f4f1ec" ear="bunny" nose="#ff8cb0" front={<Holding m={["easter", "egg"]} s={1.6} />} />,
  "ss-easter-chick": () => <Bird body="#ffe14d" beak="#ff9a3c" feet={null} front={<path d="M18 74 L26 80 L34 72 L42 80 L50 72 L58 80 L66 72 L74 80 L82 74 L80 94 Q50 100 20 94 Z" fill="#fffaf0" stroke={INK} strokeWidth="2.4" strokeLinejoin="round" />} />,
  "ss-easter-lamb": Lamb,
  "ss-july4-eagle": () => <Bird body="#7a4a24" hood="#ffffff" beak="#f2c230" feet="#f2c230" front={<Bandana c="#1f3f8f" m={["", "star"]} />} />,
  "ss-july4-star-pup": () => <Critter fur="#f4f1ec" head="#f4f1ec" ear="none" floppy="#3a2a22" front={<Bandana c="#e23b4a" m={["july4", "star"]} />} />,
  "ss-july4-turtle": Turtle,
  "ss-halloween-black-cat": () => <Critter fur="#3a3445" head="#3a3445" ear="pointy" earIn="#ff9a3c" nose="#ff7aa8" whiskers front={<g><ellipse cx="41" cy="46" rx="4.6" ry="5.2" fill="#c6f26b" stroke={INK} strokeWidth="1.2" /><ellipse cx="59" cy="46" rx="4.6" ry="5.2" fill="#c6f26b" stroke={INK} strokeWidth="1.2" /><ellipse cx="41" cy="46.4" rx="1.6" ry="3.6" fill={INK} /><ellipse cx="59" cy="46.4" rx="1.6" ry="3.6" fill={INK} /><circle cx="42.6" cy="44" r="1.2" fill="#fff" /><circle cx="60.6" cy="44" r="1.2" fill="#fff" /><Collar c="#ff8a1f" tag={["halloween", "pumpkin"]} /></g>} />,
  "ss-halloween-ghost": Ghost,
  "ss-halloween-bat": () => (
    <g>
      <BatW c="#5b3a8c" c2="#8e6ac4" />
      <Critter fur="#6b4aa0" head="#7b58b2" ear="pointy" earIn="#c9b2ef" nose="#ff7aa8" headR={22} front={<path d="M45 61 l2 4 2 -3 M51 62 l2 3 2 -4" stroke="#fff" strokeWidth="1.6" fill="none" />} />
    </g>
  ),
  "ss-diwali-elephant": Elephant,
  "ss-diwali-peacock": () => <Bird body="#1d6fe0" belly="#2ec4b6" beak="#f6c343" back={<g transform="translate(0 6)"><PeacockW /></g>} front={<g><path d="M50 22 L44 8 M50 22 L50 6 M50 22 L56 8" stroke={INK} strokeWidth="1.6" /><circle cx="44" cy="8" r="2.6" fill="#2ec4b6" stroke={INK} strokeWidth="1" /><circle cx="50" cy="6" r="2.6" fill="#2ec4b6" stroke={INK} strokeWidth="1" /><circle cx="56" cy="8" r="2.6" fill="#2ec4b6" stroke={INK} strokeWidth="1" /></g>} />,
  "ss-diwali-tiger": () => (
    <Critter
      fur="#ff9f43"
      head="#ffa94d"
      belly="#fff3e0"
      ear="round"
      earIn="#fff3e0"
      snout="#fff3e0"
      front={
        <g>
          <TigerStripes />
          {Array.from({ length: 7 }, (_, i) => {
            const [x, y] = qpt([28, 66], [50, 82], [72, 66], i / 6);
            return <Put key={i} m={["diwali", "marigold"]} x={x} y={y} s={0.75} />;
          })}
        </g>
      }
    />
  ),
  "ss-thanksgiving-turkey": () => (
    <Bird
      body="#8a4b22"
      belly="#a0602e"
      beak="#f2c230"
      back={
        <g>
          {["#e5383b", "#ff8a1f", "#f2c230", "#7fb03a", "#f2c230", "#ff8a1f", "#e5383b"].map((c, i) => (
            <ellipse key={i} cx="50" cy="20" rx="9" ry="26" fill={c} stroke={INK} strokeWidth="2.2" transform={`rotate(${-75 + i * 25} 50 60)`} />
          ))}
        </g>
      }
      front={<path d="M52 58 q5 8 -1 12" stroke="#e5383b" strokeWidth="4.5" fill="none" strokeLinecap="round" />}
    />
  ),
  "ss-thanksgiving-squirrel": () => <Critter fur="#b8662e" head="#c8753a" belly="#f4d8b4" ear="pointy" earIn="#f4d8b4" back={<BigTail c="#b8662e" c2="#f4d8b4" />} front={<Holding m={["thanksgiving", "acorn"]} s={1.6} />} />,
  "ss-thanksgiving-fox": () => <Critter fur="#e8732e" head="#f08236" belly="#fff3e0" ear="pointy" earIn="#3a2a22" snout="#fff3e0" back={<BigTail c="#e8732e" c2="#fff" />} front={<Scarf c="#f2b134" c2="#8a4b22" />} />,
  "ss-hanukkah-pup": () => (
    <Critter
      fur="#2a5bd7"
      head="#e8d2b0"
      ear="none"
      floppy="#a8693a"
      front={
        <g>
          <path d="M26 72 Q50 84 74 72" stroke="#ffffff" strokeWidth="3" fill="none" />
          <Put m={["hanukkah", "dreidel"]} x={50} y={84} s={1.1} />
        </g>
      }
    />
  ),
  "ss-hanukkah-polar-bear": () => <Critter fur="#f7f9fc" ear="round" earIn="#dfe6ef" snout="#ffffff" front={<Scarf c="#2a5bd7" c2="#c9d3e6" />} />,
  "ss-hanukkah-kitten": () => <Critter fur="#b8c2d4" head="#c9d1df" ear="pointy" nose="#ff7aa8" whiskers front={<Holding m={["hanukkah", "gelt"]} s={1.5} />} />,
  "ss-christmas-reindeer": () => <Critter fur="#a5683a" head="#b97a46" ear="side" snout="#e8c49a" nose="#5a3418" back={<Antlers />} front={<Collar c="#d8343b" tag={["christmas", "bell"]} />} />,
  "ss-christmas-snowman": Snowman,
  "ss-christmas-penguin": () => <Bird body="#2a2f3e" belly="#ffffff" face="#ffffff" beak="#ff9a3c" front={<g><MiniHat kind="santa" /><Scarf c="#2fae5b" c2="#d8343b" /></g>} />,
  "ss-kwanzaa-lion": () => <Critter fur="#e8b04a" head="#f2c25a" ear="round" earIn="#f9dc9a" snout="#fff0c8" back={<Mane c="#b8692e" />} front={<Collar c="#1f8a3e" tag={["kwanzaa", "cup"]} />} />,
  "ss-kwanzaa-giraffe": Giraffe,
  "ss-kwanzaa-hippo": () => <Critter fur="#9b8fbf" head="#a99dcc" ear="tiny" snout="#c3b8e3" nose="#6b5e94" front={<Scarf c="#c62f2f" c2="#2a2420" />} />,
};

// ===========================================================================
// FRAMES — a ring around the avatar circle (r≈45 at 50,50)
// ===========================================================================

const MotifRing = ({ outer, inner, ms, n = 8, s = 1.1, dash }) => (
  <g>
    <circle cx="50" cy="50" r="45" fill="none" stroke={outer} strokeWidth="7" />
    <circle cx="50" cy="50" r="45" fill="none" stroke={inner} strokeWidth="2.6" strokeDasharray={dash} />
    {Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return <Put key={i} m={ms[i % ms.length]} x={50 + Math.cos(a) * 45} y={50 + Math.sin(a) * 45} s={s} />;
    })}
  </g>
);
const WreathRing = ({ leaf, ms, s = 1.1, n = 6 }) => (
  <g>
    <circle cx="50" cy="50" r="45" fill="none" stroke={shade(leaf, 0.7)} strokeWidth="5" />
    {Array.from({ length: 28 }, (_, i) => {
      const a = (i / 28) * Math.PI * 2;
      const x = 50 + Math.cos(a) * 45;
      const y = 50 + Math.sin(a) * 45;
      return <ellipse key={i} cx={x} cy={y} rx="2.4" ry="5" fill={i % 2 ? leaf : shade(leaf, 1.2)} stroke={INK} strokeWidth="0.8" transform={`rotate(${(a * 180) / Math.PI + (i % 2 ? 40 : -40)} ${x} ${y})`} />;
    })}
    {Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return <Put key={i} m={ms[i % ms.length]} x={50 + Math.cos(a) * 45} y={50 + Math.sin(a) * 45} s={s} />;
    })}
  </g>
);
const LightsRing = ({ wire = "#1f5c2e", cols }) => (
  <g>
    <circle cx="50" cy="50" r="45" fill="none" stroke={wire} strokeWidth="2.6" />
    {Array.from({ length: 16 }, (_, i) => {
      const a = (i / 16) * Math.PI * 2;
      const x = 50 + Math.cos(a) * 45;
      const y = 50 + Math.sin(a) * 45;
      return (
        <g key={i} transform={`translate(${x} ${y}) rotate(${(a * 180) / Math.PI + 90})`}>
          <rect x="-1.6" y="-1" width="3.2" height="2.6" rx="0.6" fill="#2a3a2a" />
          <ellipse cx="0" cy="-4.4" rx="2.8" ry="4" fill={cols[i % cols.length]} stroke={INK} strokeWidth="0.9" />
          <ellipse cx="-0.9" cy="-5.2" rx="0.8" ry="1.3" fill="#fff" opacity="0.7" />
        </g>
      );
    })}
  </g>
);

const FRAMES = {
  "ss-newyear-gold-ring": () => <MotifRing outer="#1f2a5c" inner="#f2c230" ms={[["", "star"], ["newyear", "firework"]]} />,
  "ss-newyear-confetti-ring": () => <MotifRing outer="#f2c230" inner="#fff3c4" ms={[["", "confetti"], ["newyear", "clock"], ["", "confetti"], ["newyear", "discoball"]]} n={8} dash="2 4" />,
  "ss-lunarnewyear-lantern-ring": () => <MotifRing outer="#e03131" inner="#f6c343" ms={[["lunarnewyear", "lantern"], ["lunarnewyear", "mandarin"]]} />,
  "ss-lunarnewyear-blossom-ring": () => <WreathRing leaf="#8a5a36" ms={[["", "blossom"]]} n={10} s={1} />,
  "ss-valentines-heart-ring": () => <MotifRing outer="#ff8cc6" inner="#ffe4ec" ms={[["valentines", "heart"], ["valentines", "lollipop"]]} />,
  "ss-valentines-rose-ring": () => <WreathRing leaf="#3d9b4a" ms={[["valentines", "rose"]]} n={7} />,
  "ss-stpatricks-clover-ring": () => <WreathRing leaf="#2fbf5b" ms={[["stpatricks", "shamrock"]]} n={8} />,
  "ss-stpatricks-gold-ring": () => <MotifRing outer="#1f9e4a" inner="#f2c230" ms={[["stpatricks", "coin"], ["stpatricks", "potofgold"], ["stpatricks", "horseshoe"]]} />,
  "ss-easter-flower-ring": () => <WreathRing leaf="#7fd36b" ms={[["easter", "tulip"], ["", "blossom"]]} n={8} />,
  "ss-easter-egg-ring": () => <MotifRing outer="#b69cf2" inner="#fdf3ff" ms={[["easter", "egg"], ["easter", "chick"], ["easter", "bunny"]]} n={9} dash="3 3" />,
  "ss-july4-star-ring": () => <MotifRing outer="#1f3f8f" inner="#ffffff" ms={[["july4", "star"]]} n={10} s={0.9} dash="6 4" />,
  "ss-july4-firework-ring": () => <MotifRing outer="#e23b4a" inner="#ffffff" ms={[["july4", "firework"], ["july4", "flag"]]} />,
  "ss-halloween-pumpkin-ring": () => <MotifRing outer="#6b3fa0" inner="#ff8a1f" ms={[["halloween", "pumpkin"], ["halloween", "candy"]]} />,
  "ss-halloween-bat-ring": () => <MotifRing outer="#ff8a1f" inner="#3a3445" ms={[["halloween", "bat"], ["halloween", "ghost"]]} />,
  "ss-diwali-marigold-ring": () => <MotifRing outer="#f6c343" inner="#d6246e" ms={[["diwali", "marigold"]]} n={14} s={0.8} />,
  "ss-diwali-diya-ring": () => <MotifRing outer="#d6246e" inner="#f6c343" ms={[["diwali", "diya"], ["diwali", "rangoli"]]} />,
  "ss-thanksgiving-leaf-ring": () => <WreathRing leaf="#c8641e" ms={[["thanksgiving", "leaf"], ["thanksgiving", "acorn"]]} n={8} />,
  "ss-thanksgiving-harvest-ring": () => <MotifRing outer="#8a4b22" inner="#f2b134" ms={[["thanksgiving", "turkey"], ["thanksgiving", "pie"], ["thanksgiving", "acorn"]]} n={6} s={1.2} />,
  "ss-hanukkah-dreidel-ring": () => <MotifRing outer="#2a5bd7" inner="#c9d3e6" ms={[["hanukkah", "dreidel"], ["hanukkah", "gelt"]]} />,
  "ss-hanukkah-lights-ring": () => <LightsRing wire="#c9d3e6" cols={["#4f7ff0", "#ffffff", "#9fc0ff", "#f2c230"]} />,
  "ss-christmas-holly-ring": () => <WreathRing leaf="#2fae5b" ms={[["", "holly"], ["christmas", "ornament"]]} n={8} />,
  "ss-christmas-lights-ring": () => <LightsRing cols={["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff", "#ff9a3c", "#b07cff"]} />,
  "ss-kwanzaa-kinara-ring": () => <MotifRing outer="#2a2420" inner="#f2c230" ms={[["kwanzaa", "kinara"], ["kwanzaa", "cup"]]} n={6} s={1.2} />,
  "ss-kwanzaa-harvest-ring": () => <WreathRing leaf="#1f8a3e" ms={[["kwanzaa", "corn"], ["kwanzaa", "basket"]]} n={6} />,
};

export const SEASON_ART = { ...HATS, ...WINGS, ...PETS, ...FRAMES };

// ===========================================================================
// CLOTHES — same chest box as avatarWear.js Top (y 74.5 – 104)
// ===========================================================================

const BODY = "M8 104 C9 86 21 77.5 36 74.5 L64 74.5 C79 77.5 91 86 92 104 Z";
const PRINT_PTS = [
  [20, 96, -10],
  [33, 88, 8],
  [67, 88, -8],
  [80, 96, 10],
  [27, 101, 0],
  [73, 101, 0],
];

/**
 * Outfits: base colour + any of
 *  collar: fur | zigzag | round | mandarin | shirt | ruffle | sailor | vneck
 *  placket: fur | buttons | frog | embroidery | lapels
 *  print: [motif…] scattered, big: motif in the middle, band: Fair Isle row,
 *  stripes: [colours], plaid: [c1, c2], vest, apron, sparkle, bowtie
 * legs: colours for the full-body view (outfitLegs)
 */
const TOPS = {
  // 🎆 New Year's
  "ny-sequin": { base: "#f2c230", sparkle: "#fff8d6", collar: "round", collarC: "#d9a400", legs: { pants: "#1f1d2b", shoes: "#f2c230" } },
  "ny-tux": { base: "#1f1d2b", placket: "lapels", bowtie: "#f2c230", legs: { pants: "#1f1d2b", shoes: "#111111", trim: "#ffffff" } },
  "ny-firework-tee": { base: "#1f2a5c", collar: "round", print: [["newyear", "firework"]], big: ["newyear", "firework"], legs: { pants: "#3b5a8f", shoes: "#f2f2f2", sleeveLong: false } },
  // 🏮 Lunar New Year
  "lny-jacket": { base: "#d62828", collar: "mandarin", collarC: "#f6c343", placket: "frog", placketC: "#f6c343", print: [["", "blossom"]], legs: { pants: "#2a2420", shoes: "#2a2420", trim: "#f6c343" } },
  "lny-blossom": { base: "#e03131", collar: "mandarin", collarC: "#f6c343", print: [["", "blossom"], ["lunarnewyear", "mandarin"]], legs: { skirt: true, pants: "#e03131", shoes: "#f6c343", trim: "#f6c343" } },
  "lny-lantern-sweater": { base: "#f6c343", collar: "round", collarC: "#d62828", band: { c: "#d62828", c2: "#fff" }, print: [["lunarnewyear", "lantern"]], legs: { pants: "#3b5a8f", shoes: "#d62828" } },
  // 💝 Valentine's
  "val-heart-sweater": { base: "#e83f6f", collar: "round", collarC: "#ffb3c8", band: { c: "#ffb3c8", c2: "#fff" }, print: [["valentines", "heart"]], legs: { pants: "#3b5a8f", shoes: "#ffffff" } },
  "val-big-heart": { base: "#ffb3c8", collar: "round", collarC: "#e83f6f", big: ["valentines", "heart"], legs: { pants: "#3b5a8f", shoes: "#e83f6f", sleeveLong: false } },
  "val-sweetheart-vest": { base: "#ffffff", vest: "#c81d4e", vestButtons: "heart", collar: "shirt", bowtie: "#ff8cc6", legs: { pants: "#2a2a3a", shoes: "#c81d4e", sleeve: "#ffffff" } },
  // ☘️ St. Patrick's
  "stp-shamrock-sweater": { base: "#1f9e4a", collar: "round", collarC: "#7fd36b", band: { c: "#f2c230", c2: "#ffffff" }, print: [["stpatricks", "shamrock"]], legs: { pants: "#3b5a8f", shoes: "#1f9e4a" } },
  "stp-vest": { base: "#ffffff", vest: "#1f9e4a", vestButtons: "gold", collar: "shirt", bowtie: "#f2c230", legs: { pants: "#1f7a3a", shoes: "#2a2420", sleeve: "#ffffff" } },
  "stp-gold-tee": { base: "#2fbf5b", collar: "round", collarC: "#1f7a3a", big: ["stpatricks", "potofgold"], print: [["stpatricks", "coin"]], legs: { pants: "#3b5a8f", shoes: "#f2c230", sleeveLong: false } },
  // 🐣 Easter
  "eas-cardigan": { base: "#b69cf2", collar: "vneck", collarC: "#7fd8be", placket: "buttons", placketC: "#ffe27a", print: [["easter", "egg"]], legs: { pants: "#7fd8be", shoes: "#ffffff" } },
  "eas-bunny-tee": { base: "#ffe27a", collar: "round", collarC: "#b69cf2", big: ["easter", "bunny"], legs: { pants: "#3b5a8f", shoes: "#b69cf2", sleeveLong: false } },
  "eas-spring-dress": { base: "#ff9ec4", collar: "ruffle", print: [["easter", "tulip"], ["", "blossom"]], legs: { skirt: true, pants: "#ff9ec4", shoes: "#ffffff", sleeveLong: false } },
  // 🎇 4th of July
  "j4-flag-tee": { base: "#ffffff", stripes: ["#e23b4a", "#ffffff"], canton: true, collar: "round", collarC: "#1f3f8f", legs: { pants: "#3b5a8f", shoes: "#e23b4a", sleeveLong: false } },
  "j4-star-vest": { base: "#ffffff", vest: "#1f3f8f", vestButtons: "star", collar: "shirt", bowtie: "#e23b4a", legs: { pants: "#e23b4a", shoes: "#1f3f8f", sleeve: "#ffffff" } },
  "j4-sailor": { base: "#ffffff", stripes: ["#ffffff", "#e23b4a"], collar: "sailor", collarC: "#1f3f8f", legs: { pants: "#1f3f8f", shoes: "#ffffff" } },
  // 🎃 Halloween
  "hw-pumpkin-sweater": { base: "#ff8a1f", collar: "round", collarC: "#2a2130", big: ["halloween", "pumpkin"], print: [["halloween", "bat"], ["halloween", "candy"]], legs: { pants: "#2a2130", shoes: "#ff8a1f" } },
  "hw-skeleton": { base: "#2a2130", collar: "round", collarC: "#4a3f55", bones: true, legs: { pants: "#2a2130", shoes: "#ffffff", bones: true } },
  "hw-witch-dress": { base: "#6b3fa0", collar: "zigzag", collarC: "#2a2130", placket: "buttons", placketC: "#7ed957", print: [["", "star"]], legs: { skirt: true, pants: "#6b3fa0", shoes: "#2a2130", stripes: "#7ed957" } },
  // 🪔 Diwali
  "dw-kurta": { base: "#ff9f1c", collar: "mandarin", collarC: "#f6c343", placket: "embroidery", placketC: "#f6c343", legs: { pants: "#fff3dc", shoes: "#d6246e", trim: "#f6c343" } },
  "dw-festive-top": { base: "#d6246e", collar: "vneck", collarC: "#f6c343", placket: "embroidery", placketC: "#f6c343", mirrors: true, legs: { skirt: true, pants: "#d6246e", shoes: "#f6c343", trim: "#f6c343" } },
  "dw-rangoli-tee": { base: "#2ec4b6", collar: "round", collarC: "#1d8a80", big: ["diwali", "rangoli"], print: [["diwali", "diya"]], legs: { pants: "#3b5a8f", shoes: "#ff9f1c", sleeveLong: false } },
  // 🦃 Thanksgiving
  "tg-flannel": { base: "#c0392b", plaid: ["#7a1f16", "#f2b134"], collar: "shirt", collarC: "#c0392b", placket: "buttons", placketC: "#f4e4c8", legs: { pants: "#3b5a8f", shoes: "#7a4a24" } },
  "tg-turkey-sweater": { base: "#f2b134", collar: "round", collarC: "#8a4b22", big: ["thanksgiving", "turkey"], print: [["thanksgiving", "leaf"]], legs: { pants: "#6b4a2b", shoes: "#8a4b22" } },
  "tg-leaf-vest": { base: "#f4e4c8", vest: "#c8641e", vestButtons: "acorn", collar: "round", collarC: "#c9b38f", print: [["thanksgiving", "leaf"]], legs: { pants: "#6b4a2b", shoes: "#3a2a22", sleeve: "#f4e4c8" } },
  // 🕎 Hanukkah
  "hk-dreidel-sweater": { base: "#2a5bd7", collar: "round", collarC: "#c9d3e6", band: { c: "#ffffff", c2: "#c9d3e6" }, print: [["hanukkah", "dreidel"]], legs: { pants: "#2a2a3a", shoes: "#c9d3e6" } },
  "hk-silver-stripes": { base: "#c9d3e6", stripes: ["#2a5bd7", "#c9d3e6", "#ffffff", "#c9d3e6"], collar: "round", collarC: "#2a5bd7", legs: { pants: "#1f2f5c", shoes: "#2a5bd7" } },
  "hk-lights-tee": { base: "#1f2f5c", collar: "round", collarC: "#2a5bd7", big: ["hanukkah", "menorah"], print: [["hanukkah", "gelt"]], legs: { pants: "#3b5a8f", shoes: "#f2c230", sleeveLong: false } },
  // 🎄 Christmas
  "xm-santa": { base: "#d8343b", collar: "fur", placket: "fur", placketButtons: "#2a2130", legs: { pants: "#d8343b", shoes: "#1f1f1f", belt: "#1f1f1f", trim: "#ffffff" } },
  "xm-elf": { base: "#2fae5b", collar: "zigzag", collarC: "#d8343b", bells: true, placket: "buttons", placketC: "#f2c230", legs: { pants: "#ffffff", stripes: "#d8343b", shoes: "#2fae5b", belt: "#7a4a1f" } },
  "xm-mrs-claus": { base: "#d8343b", collar: "fur", apron: "#ffffff", legs: { skirt: true, pants: "#d8343b", shoes: "#1f1f1f", trim: "#ffffff" } },
  // 🕯️ Kwanzaa
  "kw-woven-sweater": { base: "#2a2420", stripes: ["#c62f2f", "#2a2420", "#f2c230", "#1f8a3e", "#2a2420"], collar: "round", collarC: "#f2c230", weave: true, legs: { pants: "#2a2420", shoes: "#c62f2f" } },
  "kw-festive-print": { base: "#f2c230", collar: "vneck", collarC: "#2a2420", panel: ["#c62f2f", "#2a2420", "#1f8a3e"], legs: { pants: "#2a2420", shoes: "#c62f2f", trim: "#c62f2f" } },
  "kw-kinara-tee": { base: "#1f8a3e", collar: "round", collarC: "#c62f2f", big: ["kwanzaa", "kinara"], print: [["kwanzaa", "corn"]], legs: { pants: "#3b5a8f", shoes: "#f2c230", sleeveLong: false } },
};

const DEFAULT_LEGS = { pants: "#3b5a8f", shoes: "#2a2a2a" };

/**
 * Colours for the full-body view: body (garment below the chest), sleeve
 * (+ sleeveLong), trim (cuffs/hem), belt, pants, shoes, stripes (tights),
 * skirt (true → draw a skirt in `pants` colour), bones (skeleton legs).
 */
export const outfitLegs = (style) => {
  const t = TOPS[style];
  if (!t) return { ...DEFAULT_LEGS, body: null, sleeve: null, sleeveLong: true };
  const l = t.legs || {};
  return {
    pants: l.pants || DEFAULT_LEGS.pants,
    shoes: l.shoes || DEFAULT_LEGS.shoes,
    // a costume (its own trousers: a dress, tights, a tux, Santa's suit…)
    // keeps its own legs and shoes; with everyday jeans (#3b5a8f) the
    // student's own pants and shoes from the maker show instead
    ownPants: Boolean(l.skirt || l.stripes || l.bones || (l.pants && l.pants !== DEFAULT_LEGS.pants)),
    ownShoes: Boolean(l.skirt || l.stripes || l.bones || (l.pants && l.pants !== DEFAULT_LEGS.pants)),
    body: l.body || t.base,
    sleeve: l.sleeve || t.base,
    sleeveLong: l.sleeveLong !== false,
    ...(l.trim ? { trim: l.trim } : {}),
    ...(l.belt ? { belt: l.belt } : {}),
    ...(l.stripes ? { stripes: l.stripes } : {}),
    ...(l.skirt ? { skirt: true } : {}),
    ...(l.bones ? { bones: true } : {}),
  };
};

export const TOP_STYLES = Object.keys(TOPS);

const Fur = ({ d, w = 6 }) => (
  <g>
    <path d={d} stroke="#c9d1db" strokeWidth={w + 1.6} fill="none" strokeLinecap="round" />
    <path d={d} stroke="#ffffff" strokeWidth={w} fill="none" strokeLinecap="round" />
  </g>
);

export const SeasonTop = ({ style, rid = "st" }) => {
  const t = TOPS[style];
  if (!t) return null;
  const base = t.base;
  const ink = shade(base === "#ffffff" ? "#c3c8d2" : base, 0.55);
  const clip = `${rid}stclip`;
  const neck = "M40 74.5 Q50 84 60 74.5";
  return (
    <g>
      <defs>
        <clipPath id={clip}>
          <path d={BODY} />
        </clipPath>
      </defs>
      <path d={BODY} fill={base} />
      <g clipPath={`url(#${clip})`}>
        {t.stripes &&
          Array.from({ length: 10 }, (_, i) => (
            <rect key={i} x="0" y={74 + i * 3.4} width="100" height="3.4" fill={t.stripes[i % t.stripes.length]} />
          ))}
        {t.weave &&
          Array.from({ length: 12 }, (_, i) => (
            <rect key={`w${i}`} x={6 + i * 8} y={80 + (i % 2) * 7} width="4" height="4" fill={i % 3 ? "#f2c230" : "#c62f2f"} />
          ))}
        {t.canton && (
          <g>
            <rect x="8" y="74" width="30" height="18" fill="#1f3f8f" />
            {[[16, 80], [24, 79], [32, 80], [20, 86], [28, 86]].map(([x, y], i) => (
              <path key={i} d={starPath(x, y, 2.2)} fill="#fff" />
            ))}
          </g>
        )}
        {t.plaid && (
          <g>
            {[14, 30, 46, 62, 78].map((x) => (
              <rect key={`v${x}`} x={x} y="70" width="5" height="40" fill={t.plaid[0]} opacity="0.55" />
            ))}
            {[80, 92].map((y) => (
              <rect key={`h${y}`} x="0" y={y} width="100" height="4" fill={t.plaid[0]} opacity="0.55" />
            ))}
            {[17, 49, 81].map((x) => (
              <rect key={`t${x}`} x={x} y="70" width="1" height="40" fill={t.plaid[1]} />
            ))}
            <rect x="0" y="86" width="100" height="1" fill={t.plaid[1]} />
          </g>
        )}
        {t.band && (
          <g>
            <rect x="0" y="85" width="100" height="8" fill={t.band.c} />
            {Array.from({ length: 13 }, (_, i) => (
              <path key={i} d={`M${i * 8} 89 l4 -3 4 3 -4 3 Z`} fill={t.band.c2} />
            ))}
          </g>
        )}
        {t.sparkle &&
          Array.from({ length: 34 }, (_, i) => (
            <circle key={i} cx={8 + rnd(i) * 84} cy={76 + rnd(i + 40) * 28} r={0.6 + rnd(i + 9) * 0.7} fill={i % 3 ? t.sparkle : "#ffffff"} opacity="0.9" />
          ))}
        {t.print && PRINT_PTS.map(([x, y, r], i) => <Put key={i} m={t.print[i % t.print.length]} x={x} y={y} s={0.8} r={r} />)}
        {t.bones && (
          <g stroke="#f4f1ec" strokeWidth="2.2" fill="none" strokeLinecap="round">
            <path d="M50 82 L50 104" />
            {[86, 91, 96, 101].map((y) => (
              <path key={y} d={`M50 ${y} Q40 ${y - 2.6} 33 ${y + 1.6} M50 ${y} Q60 ${y - 2.6} 67 ${y + 1.6}`} />
            ))}
          </g>
        )}
        {t.panel && (
          <g>
            <path d="M38 74.5 L50 96 L62 74.5 Z" fill={t.panel[1]} />
            {Array.from({ length: 6 }, (_, i) => (
              <path key={i} d={`M${40 + i * 3.6} 76 l1.8 3.4 1.8 -3.4 Z`} fill={t.panel[i % 2 ? 2 : 0]} />
            ))}
            {[0, 1, 2].map((i) => (
              <path key={`b${i}`} d={`M${44 + i * 3.4} 84 l1.7 3.2 1.7 -3.2 Z`} fill={t.panel[i % 2 ? 0 : 2]} />
            ))}
            <path d="M38 74.5 L50 96 L62 74.5" fill="none" stroke="#f2c230" strokeWidth="1.6" />
            <path d="M12 98 L88 98" stroke={t.panel[0]} strokeWidth="2.4" strokeDasharray="4 3" />
          </g>
        )}
        {t.mirrors &&
          [[24, 92], [34, 98], [66, 98], [76, 92], [30, 86], [70, 86]].map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="2" fill="#f6c343" stroke={INK} strokeWidth="0.4" />
              <circle cx={x} cy={y} r="1" fill="#e9f4ff" />
            </g>
          ))}
      </g>

      {/* vest over a shirt */}
      {t.vest && (
        <g stroke={shade(t.vest, 0.6)} strokeWidth="1" strokeLinejoin="round">
          <path d="M8 104 C9 86 21 77.5 36 74.5 L41 76 L47 104 Z" fill={t.vest} />
          <path d="M92 104 C91 86 79 77.5 64 74.5 L59 76 L53 104 Z" fill={t.vest} />
          {[88, 96].map((y) =>
            t.vestButtons === "heart" ? (
              <path key={y} d={heartPath(48.6, y, 0.32)} fill="#ff8cc6" stroke={INK} strokeWidth="0.4" />
            ) : t.vestButtons === "star" ? (
              <path key={y} d={starPath(48.4, y, 1.8)} fill="#fff" stroke="none" />
            ) : t.vestButtons === "acorn" ? (
              <circle key={y} cx="48.4" cy={y} r="1.3" fill="#7a4a24" />
            ) : (
              <circle key={y} cx="48.4" cy={y} r="1.2" fill="#f2c230" />
            )
          )}
        </g>
      )}

      {/* apron (Mrs. Claus) */}
      {t.apron && (
        <g>
          <path d="M37 76 L41 84 M63 76 L59 84" stroke={t.apron} strokeWidth="2" />
          <path d="M40 84 H60 V104 H40 Z" fill={t.apron} stroke="#c9d1db" strokeWidth="0.8" />
          <path d="M40 84 q2.5 -2 5 0 q2.5 -2 5 0 q2.5 -2 5 0 q2.5 -2 5 0" fill="none" stroke="#c9d1db" strokeWidth="0.8" />
          <path d={heartPath(50, 92, 0.5)} fill="#d8343b" />
          <path d="M40 101 H60" stroke="#2fae5b" strokeWidth="1.4" />
        </g>
      )}

      {/* placket */}
      {t.placket === "fur" && (
        <g>
          <Fur d="M50 80 L50 104" w={6.4} />
          {t.placketButtons && [88, 96].map((y) => <circle key={y} cx="50" cy={y} r="1.3" fill={t.placketButtons} />)}
        </g>
      )}
      {t.placket === "buttons" && [86, 92.5, 99].map((y) => <circle key={y} cx="50" cy={y} r="1.4" fill={t.placketC} stroke={INK} strokeWidth="0.5" />)}
      {t.placket === "frog" && (
        <g stroke={t.placketC} strokeWidth="1.1" fill="none" strokeLinecap="round">
          <path d="M50 79 L50 104" stroke={shade(base, 0.7)} strokeWidth="0.8" />
          {[84, 91, 98].map((y) => (
            <g key={y}>
              <path d={`M44.5 ${y} H55.5`} />
              <circle cx="44" cy={y} r="1.2" fill={t.placketC} />
              <circle cx="56" cy={y} r="1.2" fill={t.placketC} />
            </g>
          ))}
        </g>
      )}
      {t.placket === "embroidery" && (
        <g>
          <rect x="47.5" y="79" width="5" height="25" fill={t.placketC} />
          {[83, 88, 93, 98].map((y) => (
            <circle key={y} cx="50" cy={y} r="1" fill={base} />
          ))}
          <path d="M47.5 79 V104 M52.5 79 V104" stroke={shade(t.placketC, 0.75)} strokeWidth="0.5" />
        </g>
      )}
      {t.placket === "lapels" && (
        <g>
          <path d="M41 75 L50 92 L59 75 Z" fill="#ffffff" />
          <path d="M36 75.5 L41 75 L50 92 L46 104 L42 104 Z" fill={shade(base, 1.25)} stroke={ink} strokeWidth="0.8" />
          <path d="M64 75.5 L59 75 L50 92 L54 104 L58 104 Z" fill={shade(base, 1.25)} stroke={ink} strokeWidth="0.8" />
          {[95, 100].map((y) => (
            <circle key={y} cx="50" cy={y} r="1" fill="#f2c230" />
          ))}
        </g>
      )}

      {/* big picture on the chest */}
      {t.big && <Put m={t.big} x={50} y={93} s={1.25} />}

      <path d={BODY} fill="none" stroke={ink} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M22 88 C24 94 25 99 25 104 M78 88 C76 94 75 99 75 104" stroke={ink} strokeWidth="0.9" fill="none" opacity="0.6" />

      {/* collars */}
      {t.collar === "round" && (
        <g>
          <path d={neck} fill="none" stroke={t.collarC || shade(base, 0.8)} strokeWidth="2.6" />
          <path d={neck} fill="none" stroke={ink} strokeWidth="0.7" />
        </g>
      )}
      {t.collar === "vneck" && (
        <g>
          <path d="M40 74.5 L50 87 L60 74.5" fill="none" stroke={t.collarC} strokeWidth="2.6" strokeLinejoin="round" />
          <path d="M41.5 74.5 L50 85 L58.5 74.5" fill="none" stroke={ink} strokeWidth="0.5" />
        </g>
      )}
      {t.collar === "fur" && (
        <g>
          <Fur d="M35 75.5 Q50 89 65 75.5" w={6.4} />
          {[38, 44, 50, 56, 62].map((x, i) => (
            <circle key={x} cx={x} cy={i === 2 ? 82.4 : i % 4 ? 80.2 : 77.6} r="1.2" fill="#e6ebf2" />
          ))}
        </g>
      )}
      {t.collar === "zigzag" && (
        <g>
          <path d="M34 75 L38 84 L42 78 L46 86.5 L50 80 L54 86.5 L58 78 L62 84 L66 75 Q50 81 34 75 Z" fill={t.collarC} stroke={shade(t.collarC, 0.6)} strokeWidth="0.8" strokeLinejoin="round" />
          {t.bells &&
            [[38, 84], [46, 86.5], [54, 86.5], [62, 84]].map(([x, y]) => (
              <circle key={x} cx={x} cy={y + 1.2} r="1.4" fill="#f2c230" stroke={INK} strokeWidth="0.4" />
            ))}
        </g>
      )}
      {t.collar === "mandarin" && (
        <g>
          <path d="M40.5 73 Q50 77.5 59.5 73 L60 77 Q50 82 40 77 Z" fill={t.collarC} stroke={shade(t.collarC, 0.6)} strokeWidth="0.7" strokeLinejoin="round" />
          <path d="M50 79.4 L50 82" stroke={shade(t.collarC, 0.6)} strokeWidth="0.7" />
        </g>
      )}
      {t.collar === "shirt" && (
        <g>
          <path d="M42 74 L50 81 L46.5 85 L37.5 77.5 Z M58 74 L50 81 L53.5 85 L62.5 77.5 Z" fill={t.collarC && t.collarC !== base ? t.collarC : "#ffffff"} stroke="#b9bfca" strokeWidth="0.7" strokeLinejoin="round" />
        </g>
      )}
      {t.collar === "ruffle" && (
        <g>
          <path d="M35.5 75.5 q2.4 4 5 2.6 q2.4 4.4 5 2.4 q2.2 4.2 4.5 1 q2.3 3.2 4.5 -1 q2.6 2 5 -2.4 q2.6 1.4 5 -2.6" fill="#ffffff" stroke="#d6d9e0" strokeWidth="0.7" />
        </g>
      )}
      {t.collar === "sailor" && (
        <g>
          <path d="M33 75 L50 93 L67 75 L72 79 L50 100 L28 79 Z" fill={t.collarC} stroke={shade(t.collarC, 0.6)} strokeWidth="0.7" strokeLinejoin="round" />
          <path d="M31 78 L50 97 L69 78" fill="none" stroke="#ffffff" strokeWidth="1" />
          <path d="M47 93 L50 98 L53 93 L50 95 Z" fill="#e23b4a" />
        </g>
      )}
      {t.bowtie && (
        <g stroke={INK} strokeWidth="0.6" strokeLinejoin="round">
          <path d="M50 81 L44.5 78 L44.5 84 Z M50 81 L55.5 78 L55.5 84 Z" fill={t.bowtie} />
          <circle cx="50" cy="81" r="1.4" fill={shade(t.bowtie, 0.85)} />
        </g>
      )}
    </g>
  );
};

// ===========================================================================
// EYEWEAR — lens centres (40.5,46.5) and (59.5,46.5)
// ===========================================================================

const L = 40.5;
const R = 59.5;
const Y = 46.5;

const lensD = (shape, cx, m) => {
  switch (shape) {
    case "heart":
      return heartPath(cx, Y + 0.6, 1.25);
    case "star":
      return starPath(cx, Y + 0.6, 8.2);
    case "round":
      return `M${cx - 6.2} ${Y} a6.2 6.2 0 1 0 12.4 0 a6.2 6.2 0 1 0 -12.4 0 Z`;
    case "oval":
      return `M${cx - 7.4} ${Y} a7.4 5.4 0 1 0 14.8 0 a7.4 5.4 0 1 0 -14.8 0 Z`;
    case "cat": {
      const o = cx + m * 7.8;
      const i = cx - m * 6.4;
      return `M${i} ${Y - 3.6} Q${cx} ${Y - 5.4} ${o} ${Y - 7} Q${o - m * 0.6} ${Y + 2.6} ${cx + m * 3} ${Y + 5} Q${cx - m * 3} ${Y + 6.2} ${i} ${Y + 2} Z`;
    }
    case "flower": {
      let d = "";
      for (let k = 0; k < 8; k += 1) {
        const a0 = (k / 8) * Math.PI * 2;
        const a1 = ((k + 1) / 8) * Math.PI * 2;
        const x0 = cx + Math.cos(a0) * 5.6;
        const y0 = Y + Math.sin(a0) * 5.6;
        const x1 = cx + Math.cos(a1) * 5.6;
        const y1 = Y + Math.sin(a1) * 5.6;
        d += `${k ? "" : `M${x0.toFixed(2)} ${y0.toFixed(2)} `}A2.4 2.4 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} `;
      }
      return `${d}Z`;
    }
    case "square":
    default:
      return `M${cx - 7.2} ${Y - 5.4} H${cx + 7.2} Q${cx + 8} ${Y - 5.4} ${cx + 7.8} ${Y - 4.4} L${cx + 7} ${Y + 4.2} Q${cx + 6.8} ${Y + 5.4} ${cx + 5.6} ${Y + 5.4} H${cx - 5.6} Q${cx - 6.8} ${Y + 5.4} ${cx - 7} ${Y + 4.2} L${cx - 7.8} ${Y - 4.4} Q${cx - 8} ${Y - 5.4} ${cx - 7.2} ${Y - 5.4} Z`;
  }
};

const LENS = {
  clear: [["#ffffff", 0.18], ["#dff0ff", 0.28]],
  dark: [["#4b4b5c", 0.95], ["#14141c", 0.95]],
  rose: [["#ff8cc6", 0.85], ["#c81d4e", 0.9]],
  red: [["#ff6b6b", 0.88], ["#8f1a1a", 0.92]],
  green: [["#6fd38a", 0.88], ["#145a2c", 0.92]],
  blue: [["#7fb2ff", 0.88], ["#1d3f8f", 0.92]],
  gold: [["#ffe27a", 0.9], ["#b07a00", 0.92]],
  orange: [["#ffb066", 0.9], ["#a8480c", 0.92]],
  purple: [["#c9a2f2", 0.88], ["#4a2c74", 0.92]],
  teal: [["#7fe7d8", 0.88], ["#146b62", 0.92]],
  rainbow: [["#ff4d6d", 0.92], ["#ffd23f", 0.92], ["#4cd964", 0.92], ["#3a86ff", 0.92]],
};

/** shape, frame, lens, deco: [motif, where ("outer"|"bridge"|"top"), scale] */
const EYEWEAR = {
  // New Year's
  "ny-star-glasses": { shape: "star", frame: "#f2c230", lens: "clear", deco: [["", "sparkle"], "outer", 0.5] },
  "ny-party-glasses": { shape: "round", frame: "#ff5fa2", lens: "clear", deco: [["", "confetti"], "outer", 0.8] },
  "ny-gold-shades": { shape: "square", frame: "#f2c230", lens: "dark", deco: [["newyear", "firework"], "outer", 0.45] },
  "ny-disco-shades": { shape: "round", frame: "#c9d3e6", lens: "rainbow", deco: [["newyear", "discoball"], "bridge", 0.45] },
  // Lunar New Year
  "lny-red-glasses": { shape: "round", frame: "#d62828", lens: "clear", deco: [["", "blossom"], "outer", 0.55] },
  "lny-lantern-glasses": { shape: "oval", frame: "#f6c343", lens: "clear", deco: [["lunarnewyear", "lantern"], "outer", 0.45] },
  "lny-gold-shades": { shape: "cat", frame: "#d62828", lens: "gold", deco: [["", "blossom"], "outer", 0.5] },
  "lny-mandarin-shades": { shape: "round", frame: "#ff8c1a", lens: "orange", deco: [["lunarnewyear", "mandarin"], "outer", 0.45] },
  // Valentine's
  "val-heart-glasses": { shape: "heart", frame: "#e83f6f", lens: "clear" },
  "val-rose-glasses": { shape: "round", frame: "#ff8cc6", lens: "clear", deco: [["valentines", "rose"], "outer", 0.5] },
  "val-heart-shades": { shape: "heart", frame: "#c81d4e", lens: "rose" },
  "val-lovebug-shades": { shape: "cat", frame: "#ff8cc6", lens: "rose", deco: [["", "heart"], "outer", 0.45] },
  // St. Patrick's
  "stp-clover-glasses": { shape: "flower", frame: "#1f9e4a", lens: "clear", deco: [["stpatricks", "shamrock"], "outer", 0.5] },
  "stp-gold-glasses": { shape: "round", frame: "#f2c230", lens: "clear", deco: [["stpatricks", "coin"], "outer", 0.4] },
  "stp-green-shades": { shape: "square", frame: "#1f7a3a", lens: "green", deco: [["stpatricks", "shamrock"], "bridge", 0.45] },
  "stp-rainbow-shades": { shape: "round", frame: "#f2c230", lens: "rainbow" },
  // Easter
  "eas-flower-glasses": { shape: "flower", frame: "#ff7eb6", lens: "clear" },
  "eas-bunny-glasses": { shape: "round", frame: "#b69cf2", lens: "clear", deco: [["easter", "bunny"], "top", 0.5] },
  "eas-pastel-shades": { shape: "oval", frame: "#7fd8be", lens: "purple", deco: [["easter", "egg"], "outer", 0.4] },
  "eas-chick-shades": { shape: "round", frame: "#ffd23f", lens: "gold", deco: [["easter", "chick"], "outer", 0.45] },
  // 4th of July
  "j4-star-glasses": { shape: "star", frame: "#1f3f8f", lens: "clear" },
  "j4-flag-glasses": { shape: "square", frame: "#e23b4a", lens: "clear", deco: [["july4", "flag"], "outer", 0.4] },
  "j4-star-shades": { shape: "star", frame: "#e23b4a", lens: "blue" },
  "j4-firework-shades": { shape: "square", frame: "#1f3f8f", lens: "dark", deco: [["july4", "firework"], "outer", 0.45] },
  // Halloween
  "hw-pumpkin-glasses": { shape: "round", frame: "#ff8a1f", lens: "clear", deco: [["halloween", "pumpkin"], "outer", 0.45] },
  "hw-bat-glasses": { shape: "cat", frame: "#2a2130", lens: "clear", deco: [["halloween", "bat"], "bridge", 0.6] },
  "hw-spooky-shades": { shape: "cat", frame: "#6b3fa0", lens: "purple", deco: [["halloween", "ghost"], "outer", 0.45] },
  "hw-candy-shades": { shape: "round", frame: "#ff8a1f", lens: "orange", deco: [["halloween", "candy"], "outer", 0.45] },
  // Diwali
  "dw-gold-glasses": { shape: "oval", frame: "#f6c343", lens: "clear", deco: [["diwali", "rangoli"], "outer", 0.45] },
  "dw-marigold-glasses": { shape: "flower", frame: "#ff9f1c", lens: "clear", deco: [["diwali", "marigold"], "outer", 0.45] },
  "dw-diya-shades": { shape: "round", frame: "#d6246e", lens: "gold", deco: [["diwali", "diya"], "outer", 0.45] },
  "dw-sparkle-shades": { shape: "cat", frame: "#f6c343", lens: "orange", deco: [["", "sparkle"], "outer", 0.55] },
  // Thanksgiving
  "tg-leaf-glasses": { shape: "round", frame: "#8a4b22", lens: "clear", deco: [["thanksgiving", "leaf"], "outer", 0.5] },
  "tg-acorn-glasses": { shape: "oval", frame: "#c8641e", lens: "clear", deco: [["thanksgiving", "acorn"], "outer", 0.45] },
  "tg-autumn-shades": { shape: "square", frame: "#8a4b22", lens: "orange", deco: [["thanksgiving", "leaf"], "outer", 0.45] },
  "tg-turkey-shades": { shape: "round", frame: "#e5383b", lens: "gold", deco: [["thanksgiving", "turkey"], "bridge", 0.5] },
  // Hanukkah
  "hk-blue-glasses": { shape: "round", frame: "#2a5bd7", lens: "clear", deco: [["", "sparkle"], "outer", 0.45] },
  "hk-dreidel-glasses": { shape: "square", frame: "#c9d3e6", lens: "clear", deco: [["hanukkah", "dreidel"], "outer", 0.45] },
  "hk-silver-shades": { shape: "oval", frame: "#c9d3e6", lens: "blue", deco: [["hanukkah", "gelt"], "outer", 0.4] },
  "hk-lights-shades": { shape: "round", frame: "#2a5bd7", lens: "dark", deco: [["hanukkah", "menorah"], "top", 0.55] },
  // Christmas
  "xm-holly-glasses": { shape: "round", frame: "#d8343b", lens: "clear", deco: [["", "holly"], "outer", 0.55] },
  "xm-tree-glasses": { shape: "star", frame: "#2fae5b", lens: "clear", deco: [["christmas", "ornament"], "outer", 0.4] },
  "xm-snowflake-shades": { shape: "round", frame: "#5aa2ff", lens: "blue", deco: [["", "snowflake"], "outer", 0.55] },
  "xm-candy-shades": { shape: "square", frame: "#d8343b", lens: "red", deco: [["christmas", "bell"], "outer", 0.45] },
  // Kwanzaa
  "kw-gold-glasses": { shape: "round", frame: "#f2c230", lens: "clear", deco: [["kwanzaa", "corn"], "outer", 0.45] },
  "kw-stripe-glasses": { shape: "square", frame: "#c62f2f", lens: "clear", deco: [["kwanzaa", "cup"], "outer", 0.4] },
  "kw-green-shades": { shape: "oval", frame: "#1f8a3e", lens: "green", deco: [["kwanzaa", "kinara"], "top", 0.55] },
  "kw-red-shades": { shape: "cat", frame: "#2a2420", lens: "red", deco: [["", "star"], "outer", 0.4] },
};
export const EYEWEAR_STYLES = Object.keys(EYEWEAR);

export const SeasonEyewear = ({ style, rid = "sew" }) => {
  const e = EYEWEAR[style];
  if (!e) return null;
  const grad = `${rid}slens`;
  const stops = LENS[e.lens] || LENS.clear;
  const lp = lensD(e.shape, L, -1);
  const rp = lensD(e.shape, R, 1);
  const w = 2;
  const deco = e.deco;
  return (
    <g>
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2={e.lens === "rainbow" ? 1 : 0} y2={e.lens === "rainbow" ? 0 : 1}>
          {stops.map(([c, o], i) => (
            <stop key={i} offset={i / Math.max(1, stops.length - 1)} stopColor={c} stopOpacity={o} />
          ))}
        </linearGradient>
      </defs>
      <path d={`M${L - 7.4} ${Y - 3} L27.6 ${Y - 2.2} M${R + 7.4} ${Y - 3} L72.4 ${Y - 2.2}`} stroke={e.frame} strokeWidth="1.6" strokeLinecap="round" />
      <path d={lp} fill={`url(#${grad})`} />
      <path d={rp} fill={`url(#${grad})`} />
      <path d={`M${L - 4} ${Y - 2.6} l3 -1.4 M${R - 4} ${Y - 2.6} l3 -1.4`} stroke="#fff" strokeWidth="1.1" strokeLinecap="round" opacity={e.lens === "clear" ? 0.9 : 0.55} />
      <path d={lp} fill="none" stroke={e.frame} strokeWidth={w} strokeLinejoin="round" />
      <path d={rp} fill="none" stroke={e.frame} strokeWidth={w} strokeLinejoin="round" />
      <path d={`M${L + 6.4} ${Y - 1.6} Q50 ${Y - 4} ${R - 6.4} ${Y - 1.6}`} stroke={e.frame} strokeWidth={w} fill="none" strokeLinecap="round" />
      <path d={lp} fill="none" stroke={shade(e.frame, 0.5)} strokeWidth="0.35" opacity="0.6" />
      <path d={rp} fill="none" stroke={shade(e.frame, 0.5)} strokeWidth="0.35" opacity="0.6" />
      {deco && deco[1] === "outer" && (
        <g>
          <Put m={deco[0]} x={L - 8.4} y={Y - 5.6} s={deco[2]} r={-12} />
          <Put m={deco[0]} x={R + 8.4} y={Y - 5.6} s={deco[2]} r={12} />
        </g>
      )}
      {deco && deco[1] === "bridge" && <Put m={deco[0]} x={50} y={Y - 4.6} s={deco[2]} />}
      {deco && deco[1] === "top" && (
        <g>
          <Put m={deco[0]} x={L} y={Y - 8.2} s={deco[2]} />
          <Put m={deco[0]} x={R} y={Y - 8.2} s={deco[2]} />
        </g>
      )}
    </g>
  );
};

// ===========================================================================
// PINS — style "season:motif", same placement contract as avatarWear Pin
// ===========================================================================

export const SeasonPin = ({ style, x = 66, y = 88, scale = 1.05 }) => {
  const [season, name] = String(style || "").split(":");
  const art = MOTIFS[season] && MOTIFS[season][name];
  if (!art) return null;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform="translate(0.6 0.8)" opacity="0.25">
        {art}
      </g>
      {art}
    </g>
  );
};

// ===========================================================================
// HAIR DECORATIONS — over the front hair, along the top of the head
// ===========================================================================

// how far up the decoration sits for big hairstyles
const HAIR_LIFT = { afro: -9, hightop: -12, curlywaves: -4, curlytop: -3, puff: -1, bigcurls: -3, naturalcurls: -3 };

const BULBS = {
  multi: ["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff", "#ff9a3c", "#b07cff"],
  redgreen: ["#e23b3b", "#2fae5b"],
  gold: ["#ffd23f", "#fff3a8"],
  blue: ["#4f7ff0", "#ffffff", "#9fc0ff"],
  rwb: ["#e23b4a", "#ffffff", "#3a6fe0"],
  pastel: ["#ffb3c8", "#b69cf2", "#7fd8be", "#ffe27a"],
};

/**
 * type: lights (bulbs on a wire) | beads | flowers (motif strand) |
 *       clips (one motif each side) | crown (motifs across the top) |
 *       band (coloured headband + motif bow) | confetti
 */
const HAIRDECO = {
  // New Year's
  "ny-confetti": { type: "confetti" },
  "ny-star-clips": { type: "clips", m: ["", "star"], s: 0.7 },
  "ny-gold-tinsel": { type: "lights", cols: BULBS.gold, tinsel: true },
  // Lunar New Year
  "lny-blossom-clips": { type: "clips", m: ["", "blossom"], s: 0.8 },
  "lny-lantern-clips": { type: "clips", m: ["lunarnewyear", "lantern"], s: 0.6 },
  "lny-red-ribbon": { type: "band", c: "#d62828", m: ["lunarnewyear", "mandarin"] },
  // Valentine's
  "val-heart-clips": { type: "clips", m: ["valentines", "heart"], s: 0.65 },
  "val-heart-band": { type: "band", c: "#ff8cc6", m: ["valentines", "heart"] },
  "val-rose-crown": { type: "crown", m: [["valentines", "rose"], ["", "heart"]], s: 0.55 },
  // St. Patrick's
  "stp-shamrock-clips": { type: "clips", m: ["stpatricks", "shamrock"], s: 0.7 },
  "stp-green-band": { type: "band", c: "#1f9e4a", m: ["stpatricks", "shamrock"] },
  "stp-coin-crown": { type: "crown", m: [["stpatricks", "coin"], ["stpatricks", "shamrock"]], s: 0.5 },
  // Easter
  "eas-flower-crown": { type: "crown", m: [["easter", "tulip"], ["", "blossom"]], s: 0.55 },
  "eas-egg-clips": { type: "clips", m: ["easter", "egg"], s: 0.6 },
  "eas-pastel-beads": { type: "beads", cols: BULBS.pastel },
  // 4th of July
  "j4-star-clips": { type: "clips", m: ["july4", "star"], s: 0.6 },
  "j4-rwb-lights": { type: "lights", cols: BULBS.rwb },
  "j4-firework-band": { type: "band", c: "#1f3f8f", m: ["july4", "firework"] },
  // Halloween
  "hw-bat-clips": { type: "clips", m: ["halloween", "bat"], s: 0.7 },
  "hw-pumpkin-lights": { type: "flowers", m: ["halloween", "pumpkin"], s: 0.42 },
  "hw-candy-crown": { type: "crown", m: [["halloween", "candy"], ["halloween", "ghost"]], s: 0.5 },
  // Diwali
  "dw-marigold-strand": { type: "flowers", m: ["diwali", "marigold"], s: 0.42 },
  "dw-gold-clips": { type: "clips", m: ["diwali", "rangoli"], s: 0.6 },
  "dw-diya-lights": { type: "lights", cols: ["#ffb627", "#fff4b0", "#ff9f1c"], glow: true },
  // Thanksgiving
  "tg-leaf-crown": { type: "crown", m: [["thanksgiving", "leaf"], ["thanksgiving", "acorn"]], s: 0.55 },
  "tg-acorn-clips": { type: "clips", m: ["thanksgiving", "acorn"], s: 0.6 },
  "tg-autumn-band": { type: "band", c: "#c8641e", m: ["thanksgiving", "leaf"] },
  // Hanukkah
  "hk-blue-lights": { type: "lights", cols: BULBS.blue },
  "hk-dreidel-clips": { type: "clips", m: ["hanukkah", "dreidel"], s: 0.6 },
  "hk-gelt-crown": { type: "crown", m: [["hanukkah", "gelt"], ["", "sparkle"]], s: 0.5 },
  // Christmas — coloured lights in the hair
  "xm-multi-lights": { type: "lights", cols: BULBS.multi },
  "xm-redgreen-lights": { type: "lights", cols: BULBS.redgreen },
  "xm-gold-lights": { type: "lights", cols: BULBS.gold, glow: true },
  // Kwanzaa
  "kw-beads": { type: "beads", cols: ["#c62f2f", "#2a2420", "#1f8a3e", "#f2c230"] },
  "kw-ribbon": { type: "band", c: "#c62f2f", stripes: ["#2a2420", "#1f8a3e"], m: ["kwanzaa", "corn"] },
  "kw-gold-clips": { type: "clips", m: ["kwanzaa", "cup"], s: 0.6 },
};
export const HAIRDECO_STYLES = Object.keys(HAIRDECO);

export const HairDeco = ({ style, hair = "" }) => {
  const h = HAIRDECO[style];
  if (!h) return null;
  const lift = HAIR_LIFT[hair] || 0;
  const p0 = [28.5, 38 + lift * 0.4];
  const p1 = [50, 8 + lift * 1.6];
  const p2 = [71.5, 38 + lift * 0.4];
  const along = (n, a = 0.04, b = 0.96) => Array.from({ length: n }, (_, i) => qpt(p0, p1, p2, a + ((b - a) * i) / Math.max(1, n - 1)));
  const wire = `M${p0[0]} ${p0[1]} Q${p1[0]} ${p1[1]} ${p2[0]} ${p2[1]}`;
  switch (h.type) {
    case "lights":
      return (
        <g>
          <path d={wire} stroke={h.tinsel ? "#e0a800" : "#1f5c2e"} strokeWidth={h.tinsel ? 2.4 : 1.1} fill="none" strokeDasharray={h.tinsel ? "0.6 0.9" : undefined} />
          {along(8).map(([x, y], i) => {
            const a = Math.atan2(y - 30, x - 50) * (180 / Math.PI) + 90;
            const c = h.cols[i % h.cols.length];
            return (
              <g key={i} transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${a.toFixed(1)})`}>
                {h.glow && <circle cx="0" cy="-3" r="4.4" fill={c} opacity="0.3" />}
                <rect x="-1.1" y="-0.6" width="2.2" height="1.7" rx="0.4" fill="#2a3a2a" />
                <ellipse cx="0" cy="-3" rx="1.9" ry="2.7" fill={c} stroke={INK} strokeWidth="0.5" />
                <ellipse cx="-0.6" cy="-3.7" rx="0.5" ry="0.9" fill="#fff" opacity="0.75" />
              </g>
            );
          })}
        </g>
      );
    case "beads":
      return (
        <g>
          <path d={wire} stroke="#7a4a24" strokeWidth="0.6" fill="none" />
          {along(13).map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="1.5" fill={h.cols[i % h.cols.length]} stroke={INK} strokeWidth="0.4" />
          ))}
        </g>
      );
    case "flowers":
      return (
        <g>
          <path d={wire} stroke="#2f8a3e" strokeWidth="0.9" fill="none" />
          {along(7).map(([x, y], i) => (
            <Put key={i} m={h.m} x={x} y={y} s={h.s} />
          ))}
        </g>
      );
    case "crown":
      return (
        <g>
          {along(7, 0.12, 0.88).map(([x, y], i) => (
            <ellipse key={`l${i}`} cx={x} cy={y} rx="1.2" ry="2.6" fill="#3d9b4a" stroke={INK} strokeWidth="0.4" transform={`rotate(${i % 2 ? 60 : -60} ${x} ${y})`} />
          ))}
          {along(5, 0.12, 0.88).map(([x, y], i) => (
            <Put key={i} m={h.m[i % h.m.length]} x={x} y={y - 0.6} s={h.s} r={(x - 50) * 0.8} />
          ))}
        </g>
      );
    case "band": {
      const [bx, by] = qpt(p0, p1, p2, 0.78);
      return (
        <g>
          <path d={wire} stroke={shade(h.c, 0.6)} strokeWidth="3.4" fill="none" strokeLinecap="round" />
          <path d={wire} stroke={h.c} strokeWidth="2.4" fill="none" strokeLinecap="round" />
          {h.stripes && <path d={wire} stroke={h.stripes[0]} strokeWidth="0.8" fill="none" strokeDasharray="2 2" />}
          <g transform={`translate(${bx} ${by})`}>
            <path d="M0 0 L-4.4 -2.8 L-4.4 2.8 Z M0 0 L4.4 -2.8 L4.4 2.8 Z" fill={h.c} stroke={INK} strokeWidth="0.5" strokeLinejoin="round" />
          </g>
          <Put m={h.m} x={bx} y={by} s={0.42} />
        </g>
      );
    }
    case "clips":
      return (
        <g>
          {[qpt(p0, p1, p2, 0.16), qpt(p0, p1, p2, 0.84)].map(([x, y], i) => (
            <g key={i}>
              <path d={`M${x - 2.4} ${y + 1.6} L${x + 2.4} ${y - 1.6}`} stroke="#c9d1db" strokeWidth="1" strokeLinecap="round" transform={i ? `scale(-1 1) translate(${-2 * x} 0)` : undefined} />
              <Put m={h.m} x={x} y={y} s={h.s} r={i ? 18 : -18} />
            </g>
          ))}
        </g>
      );
    case "confetti":
      return (
        <g>
          {along(8, 0.02, 0.98).map(([x, y], i) => (
            <Put key={i} m={["", "confetti"]} x={x + (rnd(i) - 0.5) * 3} y={y + 1 + rnd(i + 3) * 3} s={0.5} r={i * 40} />
          ))}
        </g>
      );
    default:
      return null;
  }
};

// ===========================================================================
// BACKGROUNDS — a scene behind the avatar
// ===========================================================================

/** sky: [top, bottom]; motifs scattered round the edges; extra: snow | stars | bokeh | confetti; hill: ground colour */
const BGS = {
  "ny-night": { sky: ["#141a3d", "#2f3b7a"], motifs: [["newyear", "firework"], ["newyear", "firework"], ["", "sparkle"]], extra: "stars" },
  "ny-confetti": { sky: ["#fff3c4", "#ffd6e8"], motifs: [["newyear", "horn"], ["newyear", "clock"], ["newyear", "discoball"]], extra: "confetti" },
  "lny-lanterns": { sky: ["#8f1414", "#d62828"], motifs: [["lunarnewyear", "lantern"], ["lunarnewyear", "lantern"], ["lunarnewyear", "firecracker"]], extra: "bokeh", bokeh: "#f6c343" },
  "lny-blossoms": { sky: ["#ffe7c2", "#ffd0d6"], motifs: [["", "blossom"], ["lunarnewyear", "mandarin"], ["lunarnewyear", "envelope"]], hill: "#f6c343" },
  "val-hearts": { sky: ["#ffe4ec", "#ffb3c8"], motifs: [["valentines", "heart"], ["", "heart"], ["valentines", "letter"]] },
  "val-roses": { sky: ["#c81d4e", "#ff6f91"], motifs: [["valentines", "rose"], ["valentines", "heart"], ["valentines", "lollipop"]], extra: "bokeh", bokeh: "#ffd1e0" },
  "stp-meadow": { sky: ["#bfe9ff", "#e3f8e6"], motifs: [["stpatricks", "shamrock"], ["stpatricks", "potofgold"], ["stpatricks", "shamrock"]], hill: "#3fbf5b", rainbow: true },
  "stp-gold": { sky: ["#1f7a3a", "#2fbf5b"], motifs: [["stpatricks", "coin"], ["stpatricks", "shamrock"], ["stpatricks", "horseshoe"]], extra: "bokeh", bokeh: "#f2c230" },
  "eas-garden": { sky: ["#dff3ff", "#fdf3ff"], motifs: [["easter", "tulip"], ["easter", "egg"], ["easter", "chick"]], hill: "#9be08a" },
  "eas-eggs": { sky: ["#f1e6ff", "#fff6d6"], motifs: [["easter", "egg"], ["easter", "bunny"], ["", "blossom"]], extra: "confetti" },
  "j4-fireworks": { sky: ["#0f1a44", "#1f3f8f"], motifs: [["july4", "firework"], ["july4", "firework"], ["july4", "star"]], extra: "stars" },
  "j4-picnic": { sky: ["#bfe0ff", "#e6eeff"], motifs: [["july4", "flag"], ["july4", "popsicle"], ["july4", "star"]], hill: "#5cc85a" },
  "hw-night": { sky: ["#2a1745", "#5b3a8c"], motifs: [["halloween", "bat"], ["halloween", "ghost"], ["halloween", "bat"]], extra: "stars", moon: true },
  "hw-patch": { sky: ["#ffd8a8", "#ffeedd"], motifs: [["halloween", "pumpkin"], ["halloween", "candy"], ["halloween", "pumpkin"]], hill: "#8a5a36" },
  "dw-lights": { sky: ["#3a0f4a", "#7a1f6b"], motifs: [["diwali", "diya"], ["diwali", "sparkler"], ["diwali", "diya"]], extra: "bokeh", bokeh: "#ffb627" },
  "dw-rangoli": { sky: ["#fff1d6", "#ffd9a0"], motifs: [["diwali", "rangoli"], ["diwali", "marigold"], ["diwali", "rangoli"]] },
  "tg-autumn": { sky: ["#ffd8a8", "#fbead2"], motifs: [["thanksgiving", "leaf"], ["thanksgiving", "acorn"], ["thanksgiving", "leaf"]], extra: "leaves" },
  "tg-harvest": { sky: ["#ffe9c7", "#f2c48d"], motifs: [["thanksgiving", "turkey"], ["thanksgiving", "pie"], ["thanksgiving", "acorn"]], hill: "#c8641e" },
  "hk-night": { sky: ["#13265e", "#2a5bd7"], motifs: [["hanukkah", "menorah"], ["", "sparkle"], ["hanukkah", "dreidel"]], extra: "stars" },
  "hk-gelt": { sky: ["#e6efff", "#c9d3e6"], motifs: [["hanukkah", "gelt"], ["hanukkah", "dreidel"], ["hanukkah", "donut"]], extra: "confetti" },
  "xm-snow": { sky: ["#bfe0ff", "#eaf5ff"], motifs: [["christmas", "tree"], ["", "snowflake"], ["christmas", "tree"]], extra: "snow", hill: "#ffffff" },
  "xm-cozy": { sky: ["#8f1a1f", "#d8343b"], motifs: [["christmas", "ornament"], ["christmas", "stocking"], ["christmas", "bell"]], extra: "bokeh", bokeh: "#ffe27a" },
  "kw-warm": { sky: ["#fff4dc", "#ffd9a0"], motifs: [["kwanzaa", "kinara"], ["kwanzaa", "corn"], ["kwanzaa", "basket"]], stripesBottom: ["#c62f2f", "#2a2420", "#1f8a3e"] },
  "kw-night": { sky: ["#1b1512", "#3a2a22"], motifs: [["kwanzaa", "kinara"], ["kwanzaa", "cup"], ["", "star"]], extra: "bokeh", bokeh: "#f2c230" },
};
export const BG_STYLES = Object.keys(BGS);

// edge spots (x%, y% of the box) — keep the middle clear for the head
const SPOTS = [
  [12, 14, 1.25],
  [88, 16, 1.1],
  [8, 50, 0.9],
  [92, 46, 1.0],
  [50, 7, 0.7],
];

const BgScene = ({ style, h = 100, rid = "bg" }) => {
  const b = BGS[style];
  if (!b) return null;
  const k = h / 100;
  const gid = `${rid}${style}sky`;
  const groundY = h === 100 ? 92 : 152;
  return (
    <g>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={b.sky[0]} />
          <stop offset="1" stopColor={b.sky[1]} />
        </linearGradient>
      </defs>
      <rect width="100" height={h} fill={`url(#${gid})`} />
      {b.moon && <circle cx="82" cy={14 * k} r="7" fill="#fff6c8" opacity="0.9" />}
      {b.rainbow && (
        <g fill="none" opacity="0.55">
          {["#ff4d6d", "#ffd23f", "#4cd964", "#3a86ff"].map((c, i) => (
            <path key={c} d={`M${-6 + i * 4} ${groundY} A${56 - i * 4} ${56 - i * 4} 0 0 1 ${106 - i * 4} ${groundY}`} stroke={c} strokeWidth="3.4" />
          ))}
        </g>
      )}
      {b.extra === "stars" &&
        Array.from({ length: 22 }, (_, i) => <circle key={i} cx={rnd(i) * 100} cy={rnd(i + 30) * h * 0.85} r={0.5 + rnd(i + 7) * 0.7} fill="#fff" opacity={0.5 + rnd(i + 2) * 0.5} />)}
      {b.extra === "bokeh" &&
        Array.from({ length: 14 }, (_, i) => <circle key={i} cx={rnd(i + 3) * 100} cy={rnd(i + 50) * h} r={2 + rnd(i + 11) * 4} fill={b.bokeh} opacity={0.18 + rnd(i) * 0.22} />)}
      {b.extra === "snow" &&
        Array.from({ length: 26 }, (_, i) => <circle key={i} cx={rnd(i + 5) * 100} cy={rnd(i + 70) * h * 0.92} r={0.7 + rnd(i + 1) * 1.1} fill="#fff" opacity="0.95" />)}
      {b.extra === "confetti" &&
        Array.from({ length: 12 }, (_, i) => <Put key={i} m={["", "confetti"]} x={rnd(i + 9) * 100} y={rnd(i + 19) * h} s={0.6} r={i * 30} />)}
      {b.extra === "leaves" &&
        Array.from({ length: 9 }, (_, i) => <Put key={i} m={["thanksgiving", "leaf"]} x={rnd(i + 13) * 100} y={rnd(i + 23) * h} s={0.5} r={i * 47} />)}
      {SPOTS.map(([x, y, s], i) => (
        <Put key={i} m={b.motifs[i % b.motifs.length]} x={x} y={(y * k)} s={s * 1.15} r={(i % 2 ? 1 : -1) * 10} />
      ))}
      {h > 100 &&
        [
          [10, 96, 1.2],
          [90, 104, 1.2],
        ].map(([x, y, s], i) => <Put key={`t${i}`} m={b.motifs[(i + 1) % b.motifs.length]} x={x} y={y} s={s} />)}
      {b.hill && <path d={`M0 ${groundY} Q50 ${groundY - 10} 100 ${groundY} L100 ${h} L0 ${h} Z`} fill={b.hill} opacity="0.9" />}
      {!b.hill && h > 100 && <path d={`M0 ${groundY} Q50 ${groundY - 8} 100 ${groundY} L100 ${h} L0 ${h} Z`} fill={shade(b.sky[1], 0.85)} />}
      {b.stripesBottom &&
        b.stripesBottom.map((c, i) => <rect key={c} x="0" y={h - 9 + i * 3} width="100" height="3" fill={c} />)}
    </g>
  );
};

export const SeasonBg = ({ style, rid }) => <BgScene style={style} h={100} rid={rid} />;
export const SeasonBgTall = ({ style, rid }) => <BgScene style={style} h={170} rid={rid} />;

// handy for previews
export { MOTIFS, SMALL, Stamp };
